<template>
    <PanelFrame ref="panelFrame" panel-class="agenda-app" resize-directions="all" :content-class="agendaContentClass">
        <template #titlebar-left>
            <button class="panel-titlebar-btn" @click.stop="sidebarOpen = !sidebarOpen" :title="$t('pages.module.AGENDA.panel.sidebar')">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="2" width="20" height="20" rx="2" ry="2"/><line x1="9" y1="2" x2="9" y2="22"/>
                </svg>
            </button>
            <div class="agenda-nav-group">
                <button class="agenda-nav-btn" @click.stop="navPrev">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <span class="agenda-nav-label">{{ navLabel }}</span>
                <button class="agenda-nav-btn" @click.stop="navNext">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
            </div>
            <button class="agenda-today-chip" :class="{'agenda-today-chip--hidden': isTodaySelected}" @click.stop="goToday" :title="$t('pages.module.AGENDA.panel.today')">
                <svg class="agenda-today-chip-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><circle cx="12" cy="15" r="1.5" fill="currentColor" stroke="none"/></svg>
                <span class="agenda-today-chip-text">{{ $t('pages.module.AGENDA.panel.today') }}</span>
            </button>
            <button class="agenda-add-btn agenda-add-btn--row1" @click.stop="onNewEvent()" :title="$t('pages.module.AGENDA.form.newEvent')">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
        </template>
        <template #titlebar-center>
            <div class="agenda-app-titlebar-center">
                <div class="agenda-titlebar-center-left">
                    <div class="agenda-view-segmented">
                        <button class="agenda-view-seg-btn" :class="{active: viewMode === 'month'}" @click.stop="setViewMode('month')">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            {{ $t('pages.module.AGENDA.panel.monthView') }}
                        </button>
                        <button class="agenda-view-seg-btn" :class="{active: viewMode === 'day'}" @click.stop="setViewMode('day')">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                            {{ $t('pages.module.AGENDA.panel.dayView') }}
                        </button>
                    </div>
                    <button class="agenda-add-btn agenda-add-btn--titlebar-row2" @click.stop="onNewEvent()" :title="$t('pages.module.AGENDA.form.newEvent')">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    </button>
                </div>
                <div class="agenda-titlebar-center-right" @click.stop="toggleShowShared">
                    <span class="agenda-titlebar-shared-label">{{ $t('pages.module.AGENDA.panel.showShared') }}</span>
                    <span class="agenda-sidebar-toggle-track" :class="{'agenda-sidebar-toggle-track--on': showShared}">
                        <span class="agenda-sidebar-toggle-thumb"></span>
                    </span>
                </div>
            </div>
        </template>
        <template #titlebar-actions>
            <button class="panel-titlebar-btn" @click.stop="refreshCalendar" :title="$t('pages.module.AGENDA.panel.refresh')">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            </button>
        </template>
        <template #default>
            <div class="agenda-app-body-inner" ref="agendaBody">
                <div class="agenda-app-sidebar" v-if="sidebarOpen || sheetClosing" :class="{'agenda-app-sidebar--sheet': isCompact, 'agenda-app-sidebar--fullscreen': sheetFullscreen, 'agenda-app-sidebar--dragging': sheetDragging, 'agenda-app-sidebar--closing': sheetClosing, 'agenda-app-sidebar--drag-over': sidebarDragOver}" :style="isCompact && !sheetFullscreen ? {height: sheetHeight + 'px'} : isCompact ? null : {width: sidebarWidth + 'px'}" @dragover="onSidebarDragOver" @dragleave="sidebarDragOver = false" @drop="onSidebarDrop">
                    <div class="agenda-sidebar-header" @touchstart.prevent="onSheetDragStart" @touchmove.prevent="onSheetDragMove" @touchend="onSheetDragEnd">
                        <div class="agenda-sidebar-handle" v-if="isCompact"></div>
                        <div class="agenda-sidebar-header-row">
                            <span class="agenda-sidebar-date">{{ selectedDateLabel }}</span>
                            <div class="agenda-sidebar-header-actions">
                                <button class="agenda-sidebar-group-btn" :class="{'agenda-sidebar-group-btn--active': sidebarGrouped, 'agenda-sidebar-group-btn--hidden': selectedDayEvents.length <= 1}" @click.stop="toggleSidebarGrouped" @touchstart.stop.prevent @touchend.stop.prevent="toggleSidebarGrouped" :title="$t('pages.module.AGENDA.panel.groupByTime')">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                                </button>
                                <div v-if="!isCompact" class="agenda-sidebar-shared-toggle" @click.stop="toggleShowShared" @touchstart.stop.prevent @touchend.stop.prevent="toggleShowShared">
                                    <span class="agenda-sidebar-toggle-track" :class="{'agenda-sidebar-toggle-track--on': showShared}">
                                        <span class="agenda-sidebar-toggle-thumb"></span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="agenda-sidebar-events" v-if="selectedDayEvents.length > 0" :style="swipeX !== 0 ? {overflow: 'hidden'} : null">
                        <template v-if="sidebarGrouped && groupedDayEvents.length > 0">
                            <div v-for="group in groupedDayEvents" :key="group.key" class="agenda-sidebar-group">
                                <div class="agenda-sidebar-group-label">
                                    <span>{{ group.label }}</span>
                                    <span class="agenda-sidebar-group-count">{{ group.events.length }}</span>
                                </div>
                                <div class="agenda-swipe-container" v-for="evt in group.events" :key="evt.id">
                                    <template v-if="isOwn(evt)">
                                        <div class="agenda-swipe-action"
                                             :class="evt.status?.id === 2 ? 'agenda-swipe-action--pending' : 'agenda-swipe-action--complete'"
                                             v-show="swipingEventId === evt.id && swipeX > 0">
                                            <svg v-if="evt.status?.id !== 2" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                                        </div>
                                        <div class="agenda-swipe-action agenda-swipe-action--delete" v-show="swipingEventId === evt.id && swipeX < 0">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                        </div>
                                    </template>
                                    <div class="agenda-sidebar-event"
                                         :class="{'agenda-sidebar-event--completed': evt.status?.id === 2, 'agenda-sidebar-event--shared': !isOwn(evt), 'agenda-sidebar-event--drag-ready': dragReadyEventId === evt.id || touchDragReadyId === evt.id}"
                                         :style="isOwn(evt) && swipingEventId === evt.id ? {transform: 'translateX(' + swipeX + 'px)', transition: 'none', borderRadius: 0} : null"
                                         :draggable="isOwn(evt) && !isMiddleDay(evt) && touchDraggingBlockId !== evt.id"
                                         @dragstart="isOwn(evt) && onSidebarDragStart($event, evt)"
                                         @dragend="onSidebarDragEnd"
                                         @click="onSwipeEventClick(evt)"
                                         @contextmenu.prevent="onEventContextMenu($event, evt)"
                                         @pointerdown="isOwn(evt) && onSwipeStart($event, evt)"
                                         @pointermove="isOwn(evt) && onSwipeMove($event)"
                                         @pointerup="isOwn(evt) && onSwipeEnd($event, evt)"
                                         @pointercancel="onSwipeCancel"
                                         @touchstart.passive="isOwn(evt) && onLongPressStart($event, evt)"
                                         @touchend.passive="isOwn(evt) && onLongPressEnd($event)"
                                         @touchmove.passive="isOwn(evt) && onLongPressMove($event)">
                                        <div class="agenda-sidebar-event-color-bar" :style="{background: evt.color || '#007AFF'}"></div>
                                        <div class="agenda-sidebar-event-info">
                                            <span class="agenda-sidebar-event-time">{{ getTime(evt) }}</span>
                                            <span class="agenda-sidebar-event-title">{{ evt.title }}<svg v-if="evt.recurrence" class="agenda-sidebar-event-recurrence" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg></span>
                                            <span class="agenda-sidebar-event-desc" v-if="evt.description">{{ evt.description }}</span>
                                        </div>
                                        <svg v-if="!isOwn(evt)" class="agenda-sidebar-event-shared-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                                        <span class="agenda-sidebar-event-status" :class="'agenda-sidebar-event-status--' + (evt.status?.id === 2 ? 'completed' : 'pending')">
                                            {{ evt.status?.id === 2 ? $t('pages.module.AGENDA.status.completed') : $t('pages.module.AGENDA.status.pending') }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="agenda-swipe-container" v-for="evt in selectedDayEvents" :key="evt.id">
                                <template v-if="isOwn(evt)">
                                    <div class="agenda-swipe-action"
                                         :class="evt.status?.id === 2 ? 'agenda-swipe-action--pending' : 'agenda-swipe-action--complete'"
                                         v-show="swipingEventId === evt.id && swipeX > 0">
                                        <svg v-if="evt.status?.id !== 2" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                                    </div>
                                    <div class="agenda-swipe-action agenda-swipe-action--delete" v-show="swipingEventId === evt.id && swipeX < 0">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                    </div>
                                </template>
                                <div class="agenda-sidebar-event"
                                     :class="{'agenda-sidebar-event--completed': evt.status?.id === 2, 'agenda-sidebar-event--shared': !isOwn(evt), 'agenda-sidebar-event--drag-ready': dragReadyEventId === evt.id || touchDragReadyId === evt.id}"
                                     :style="isOwn(evt) && swipingEventId === evt.id ? {transform: 'translateX(' + swipeX + 'px)', transition: 'none', borderRadius: 0} : null"
                                     :draggable="isOwn(evt) && !isMiddleDay(evt) && touchDraggingBlockId !== evt.id"
                                     @dragstart="isOwn(evt) && onSidebarDragStart($event, evt)"
                                     @dragend="onSidebarDragEnd"
                                     @click="onSwipeEventClick(evt)"
                                     @contextmenu.prevent="onEventContextMenu($event, evt)"
                                     @pointerdown="isOwn(evt) && onSwipeStart($event, evt)"
                                     @pointermove="isOwn(evt) && onSwipeMove($event)"
                                     @pointerup="isOwn(evt) && onSwipeEnd($event, evt)"
                                     @pointercancel="onSwipeCancel"
                                     @touchstart.passive="isOwn(evt) && onLongPressStart($event, evt)"
                                     @touchend.passive="isOwn(evt) && onLongPressEnd($event)"
                                     @touchmove.passive="isOwn(evt) && onLongPressMove($event)">
                                    <div class="agenda-sidebar-event-color-bar" :style="{background: evt.color || '#007AFF'}"></div>
                                    <div class="agenda-sidebar-event-info">
                                        <span class="agenda-sidebar-event-time">{{ getTime(evt) }}</span>
                                        <span class="agenda-sidebar-event-title">{{ evt.title }}</span>
                                        <span class="agenda-sidebar-event-desc" v-if="evt.description">{{ evt.description }}</span>
                                    </div>
                                    <svg v-if="!isOwn(evt)" class="agenda-sidebar-event-shared-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                                    <span class="agenda-sidebar-event-status" :class="'agenda-sidebar-event-status--' + (evt.status?.id === 2 ? 'completed' : 'pending')">
                                        {{ evt.status?.id === 2 ? $t('pages.module.AGENDA.status.completed') : $t('pages.module.AGENDA.status.pending') }}
                                    </span>
                                </div>
                            </div>
                        </template>
                    </div>
                    <div class="agenda-sidebar-empty" v-else>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.3"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        <span>{{ $t('pages.module.AGENDA.panel.noEvents') }}</span>
                        <button class="agenda-sidebar-add" @click="onNewEventForDate(selectedDate)">{{ $t('pages.module.AGENDA.panel.addEvent') }}</button>
                    </div>
                </div>

                <div class="panel-divider"
                     v-if="(sidebarOpen || sheetClosing) && !isCompact && !isSidebarHidden"
                     @mousedown="startSidebarResize"
                     @touchstart.prevent="startSidebarResize">
                </div>

                <div class="agenda-app-main" @dragstart.capture="onMainDragStart" @pointerdown.capture="onMainPointerDown">
                    <template v-if="viewMode === 'month'">
                        <div class="agenda-weekday-row">
                            <span v-for="d in weekDays" :key="d" class="agenda-weekday">{{ d }}</span>
                        </div>
                        <AgendaCalendar
                            ref="calendar"
                            :year="currentYear"
                            :month="currentMonth"
                            :items="displayItems"
                            :selected-date="selectedDate"
                            :current-user-id="currentUserId"
                            :max-visible-events="maxVisibleEvents"
                            @dayClick="onDayClick"
                            @dayDblClick="onDayDblClick"
                            @eventDrop="onEventDrop"
                            @eventClick="onEventClick"
                            @eventContextMenu="onEventContextMenu"
                            @statusToggle="onStatusToggle"
                            @eventHover="showTooltip($event.evt, $event.event)"
                            @eventLeave="hideTooltip"
                        />
                    </template>

                    <template v-else>
                        <div class="agenda-day-view">
                            <div v-if="dayViewAllDayEvents.length" class="agenda-day-allday-section">
                                <span class="agenda-day-allday-label">{{ $t('pages.module.AGENDA.panel.allDay') }}</span>
                                <div v-for="evt in dayViewAllDayEvents" :key="evt.id"
                                     class="agenda-day-allday-bar"
                                     :class="{'agenda-day-allday-bar--completed': evt.status?.id === 2}"
                                     :style="{backgroundColor: evt.color || '#007AFF', color: getContrastColor(evt.color || '#007AFF')}"
                                     :data-evt-id="evt.id"
                                     @click="onAllDayEventClick(evt)"
                                     @contextmenu.prevent="onEventContextMenu($event, evt)"
                                     @mouseenter="showTooltip(evt, $event)"
                                     @mouseleave="hideTooltip">
                                    <span v-if="isOwn(evt)" class="agenda-day-allday-bar-radio" :class="{'agenda-day-allday-bar-radio--done': evt.status?.id === 2}" @click.stop="onStatusToggle({id: evt.id, status_id: evt.status?.id === 2 ? 1 : 2})"></span>
                                    <svg v-if="evt.recurrence" class="agenda-day-allday-bar-recurrence" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                                    <span class="agenda-day-allday-bar-title">{{ evt.title }}</span>
                                    <span class="agenda-day-allday-bar-time">{{ getTime(evt) }}</span>
                                </div>
                            </div>
                            <div class="agenda-day-view-timeline">
                                <div v-for="hour in dayHours" :key="hour"
                                     class="agenda-day-hour-row"
                                     :data-hour="hour"
                                     :class="{'agenda-day-hour-row--drag-over': dayDragOverHour === hour}"
                                     @dblclick="onHourDblClick(hour)"
                                     @dragover.prevent="onDayHourDragOver($event, hour)"
                                     @dragleave="onDayHourDragLeave(hour)"
                                     @drop="onDayHourDrop($event, hour)">
                                    <span class="agenda-day-hour-label">{{ String(hour).padStart(2, '0') }}:00</span>
                                    <div class="agenda-day-hour-content">
                                        <div v-for="evt in getEventsForHour(hour)" :key="evt.id"
                                             class="agenda-day-hour-event"
                                             :class="{'agenda-day-hour-event--completed': evt.status?.id === 2}"
                                             :style="{borderLeftColor: evt.color || '#007AFF'}"
                                             :data-evt-id="evt.id"
                                             :draggable="evt.user?.id === currentUserId"
                                             @dragstart.stop="onDayEventDragStart($event, evt)"
                                             @click="onEventClick(evt)"
                                             @contextmenu.prevent="onEventContextMenu($event, evt)"
                                             @mouseenter="showTooltip(evt, $event)"
                                             @mouseleave="hideTooltip"
                                             @touchstart.passive="onLongPressStart($event, evt)"
                                             @touchend.passive="onLongPressEnd($event)"
                                             @touchmove.passive="onLongPressMove($event)">
                                            <span v-if="isOwn(evt)" class="agenda-day-hour-event-radio" :class="{'agenda-day-hour-event-radio--done': evt.status?.id === 2}" @click.stop="onStatusToggle({id: evt.id, status_id: evt.status?.id === 2 ? 1 : 2})"></span>
                                            <span class="agenda-day-hour-event-time">{{ getTime(evt) }}</span>
                                            <svg v-if="evt.recurrence" class="agenda-day-hour-event-recurrence" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                                            <span class="agenda-day-hour-event-title">{{ evt.title }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </template>

        <template #overlays>
            <AgendaContextMenu
                ref="contextMenu"
                :compact="isCompact"
                :z-base="panelZBase"
                @edit="onContextEdit"
                @reminder="onContextReminder"
                @removeReminder="onContextRemoveReminder"
                @statusToggle="onStatusToggle"
                @delete="onContextDelete"
            />

            <AgendaEventForm
                ref="eventForm"
                :officers="officers"
                :compact="isCompact"
                @save="onEventSave"
            />

            <AgendaReminderPicker
                ref="reminderPicker"
                @save="onReminderSave"
            />
            <teleport to="body">
                <div v-if="tooltip.visible" ref="tooltipPanel" class="agenda-tooltip"
                     :style="{left: tooltip.x + 'px', top: tooltip.y + 'px', zIndex: panelZBase + 25}">
                    <div class="agenda-tooltip-color" :style="{background: tooltip.color}"></div>
                    <div class="agenda-tooltip-body">
                        <div class="agenda-tooltip-title">
                            <svg v-if="tooltip.isShared" class="agenda-tooltip-shared-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                            <span>{{ tooltip.title }}</span>
                        </div>
                        <div v-if="tooltip.time" class="agenda-tooltip-time">{{ tooltip.time }}</div>
                        <div v-if="tooltip.description" class="agenda-tooltip-desc">{{ tooltip.description }}</div>
                    </div>
                </div>
            </teleport>
        </template>
    </PanelFrame>
</template>

<script>
import AgendaCalendar from './AgendaCalendar.vue'
import AgendaContextMenu from './AgendaContextMenu.vue'
import AgendaEventForm from './AgendaEventForm.vue'
import AgendaReminderPicker from './AgendaReminderPicker.vue'
import PanelFrame from '../panel/PanelFrame.vue'
import { createPanelMixin } from '../panel/usePanelBase'

const MONTH_NAMES = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
]

const DEFAULTS = {
    floatingMode: false,
    x: null,
    y: null,
    width: 1100,
    height: 700,
    sidebarWidth: 260,
}

const agendaPanelMixin = createPanelMixin({
    panelId: 'agendaPanel',
    shortcut: { code: 'KeyC' },
    minWidth: 450,
    minHeight: 500,
    minSidebar: 200,
    maxSidebar: 280,
    defaults: DEFAULTS,
    showSettingsInCompact: true,
    settingsItems: ['alwaysOnTop', 'openOnLoad', 'divider', 'transparent', 'divider', 'resetWindowSize', 'resetInnerLayout'],
    dragExcludeSelectors: [
        '.agenda-nav-btn', '.agenda-nav-group', '.agenda-today-chip',
        '.agenda-add-btn', '.agenda-action-btn', '.agenda-view-segmented',
        '.agenda-titlebar-center-right', '.agenda-sidebar-shared-toggle',
    ],
    onOpen(vm) {
        if (vm.isCompact) {
            vm.sidebarOpen = false
        } else if (vm.isSidebarHidden) {
            vm.sidebarOpen = false
        }
        vm.loadCalendar()
        vm.loadOfficers()
    },
    onClose(vm) {
        vm.hideTooltip()
    },
    onCompactResize(vm) {
        vm.sidebarOpen = false
    },
    onEscape(vm) {
        if (vm.$refs.contextMenu?.visible) { vm.$refs.contextMenu.close(); return true }
        return false
    },
    extraStorageKeys(vm) {
        return {
            sidebarGrouped: vm.sidebarGrouped,
            showShared: vm.showShared,
        }
    },
    onContext(vm, ctx) {
        if (ctx.startDate) vm.goToDate(ctx.startDate)
        if (ctx.itemId) vm.onSelectEvent?.(ctx.itemId)
        if (ctx.newEvent) vm.onNewEvent(ctx.presetDate)
    },
})

export default {
    name: 'AgendaPanel',
    mixins: [agendaPanelMixin],
    components: {AgendaCalendar, AgendaContextMenu, AgendaEventForm, AgendaReminderPicker, PanelFrame},
    data() {
        const saved = this.panelLoadLayout()
        const now = new Date()
        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
        return {
            sidebarGrouped: saved.sidebarGrouped !== undefined ? saved.sidebarGrouped : true,
            showShared: saved.showShared !== undefined ? saved.showShared : true,
            viewMode: 'month',

            currentYear: now.getFullYear(),
            currentMonth: now.getMonth() + 1,
            selectedDate: todayStr,
            calendarItems: [],
            sidebarCachedEvents: null,
            sidebarCachedDate: null,
            sidebarOpen: true,

            sheetHeight: 200,
            sheetFullscreen: false,
            sheetClosing: false,
            sheetDragging: false,
            sheetDragStartY: 0,
            sheetDragStartH: 0,

            dayDragOverHour: null,
            swipingEventId: null,
            swipeX: 0,
            dragReadyEventId: null,
            touchDraggingBlockId: null,
            sidebarDragOver: false,
            touchDragReadyId: null,
            tooltip: {
                visible: false,
                title: '',
                time: '',
                description: '',
                color: '#007AFF',
                isShared: false,
                x: 0,
                y: 0,
            },
            tooltipTimer: null,
        }
    },
    computed: {
        effectivePanelWidth() {
            return this.floatingMode ? this.panelWidth : Math.min(this.panelWidth, Math.floor(this.windowWidth * 0.95))
        },
        isStaticCenter() {
            if (this.isCompact) return false
            return this.effectivePanelWidth < 800
        },
        isNarrowPanel() {
            if (this.isCompact) return false
            return this.effectivePanelWidth < 620
        },
        isSidebarHidden() {
            if (this.isCompact) return false
            return this.viewMode === 'month' && this.effectivePanelWidth < 670
        },
        isShortPanel() {
            if (this.isCompact) return false
            const h = this.floatingMode ? this.panelHeight : Math.min(this.panelHeight, Math.floor(this.windowHeight * 0.9))
            return h <= 575
        },
        maxVisibleEvents() {
            return this.isShortPanel ? 2 : 3
        },
        weekDays() {
            return ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz']
        },
        monthLabel() {
            return MONTH_NAMES[this.currentMonth - 1]
        },
        navLabel() {
            if (this.viewMode === 'day') {
                if (!this.selectedDate) return ''
                const parts = this.selectedDate.split('-')
                const day = parseInt(parts[2], 10)
                const month = parseInt(parts[1], 10)
                const dayNames = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt']
                const d = new Date(parseInt(parts[0]), month - 1, day)
                return `${day} ${MONTH_NAMES[month - 1]}, ${dayNames[d.getDay()]}`
            }
            return `${MONTH_NAMES[this.currentMonth - 1]} ${this.currentYear}`
        },
        selectedDateLabel() {
            if (!this.selectedDate) return ''
            const parts = this.selectedDate.split('-')
            const day = parseInt(parts[2], 10)
            const month = parseInt(parts[1], 10)
            const year = parseInt(parts[0], 10)
            const date = new Date(year, month - 1, day)
            const dayNames = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt']
            return `${day} ${MONTH_NAMES[month - 1]} ${year}, ${dayNames[date.getDay()]}`
        },
        isTodaySelected() {
            const now = new Date()
            const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
            return this.selectedDate === todayStr && this.currentYear === now.getFullYear() && this.currentMonth === now.getMonth() + 1
        },
        displayItems() {
            if (this.showShared) return this.calendarItems
            return this.calendarItems.filter(item => item.user?.id === this.currentUserId)
        },
        selectedDayEvents() {
            if (!this.selectedDate) return []
            const matchesDate = (item) => {
                const startDate = (item.event_at || '').substring(0, 10)
                if (startDate === this.selectedDate) return true
                if (item.event_end_at) {
                    const endDate = item.event_end_at.substring(0, 10)
                    if (startDate <= this.selectedDate && endDate >= this.selectedDate) return true
                }
                return false
            }
            if (this.sidebarCachedEvents && this.sidebarCachedDate === this.selectedDate) {
                const items = this.showShared
                    ? this.sidebarCachedEvents.slice()
                    : this.sidebarCachedEvents.filter(item => item.user?.id === this.currentUserId)
                return items.sort((a, b) => (a.event_at || '').localeCompare(b.event_at || ''))
            }
            return this.displayItems
                .filter(matchesDate)
                .sort((a, b) => (a.event_at || '').localeCompare(b.event_at || ''))
        },
        groupedDayEvents() {
            const am = []
            const pm = []
            for (const evt of this.selectedDayEvents) {
                const h = parseInt((evt.event_at || '').substring(11, 13), 10)
                if (h < 12) am.push(evt)
                else pm.push(evt)
            }
            const groups = []
            if (am.length) groups.push({key: 'am', label: this.$t('pages.module.AGENDA.panel.morning'), events: am})
            if (pm.length) groups.push({key: 'pm', label: this.$t('pages.module.AGENDA.panel.afternoon'), events: pm})
            return groups
        },
        dayViewAllDayEvents() {
            if (!this.selectedDate) return []
            return this.selectedDayEvents.filter(evt => {
                if (!evt.event_end_at) return false
                const sd = (evt.event_at || '').substring(0, 10)
                const ed = evt.event_end_at.substring(0, 10)
                return sd !== ed && sd < this.selectedDate && ed > this.selectedDate
            })
        },
        dayHours() {
            const hours = []
            for (let h = 0; h < 24; h++) hours.push(h)
            return hours
        },
        currentUserId() {
            return this.$store.state.AuthModule?.user?.id || null
        },
        officers() {
            return this.$handler.getMembers()
        },
        agendaContentClass() {
            return {
                'agenda-app-panel--static-center': this.isStaticCenter,
                'agenda-app-panel--no-sidebar': this.isSidebarHidden,
                'agenda-app-panel--narrow': this.isNarrowPanel,
                'agenda-app-panel--short': this.isShortPanel,
            }
        },
    },
    watch: {
        isSidebarHidden(val) {
            if (val && this.sidebarOpen) this.sidebarOpen = false
        },
    },
    created() {
        const watchPath = this.$handler.getContextWatchPath()
        if (watchPath) {
            this._contextUnwatch = this.$watch(watchPath, (val) => {
                if (val && this.visible && this.$handler.getMembers().length === 0) {
                    this.loadOfficers()
                }
            })
        }
    },
    methods: {
        isOwn(evt) {
            return evt.user?.id === this.currentUserId
        },
        getTime(evt) {
            if (!evt.event_at) return ''
            const start = evt.event_at.substring(11, 16)
            if (evt.event_end_at) {
                const startDate = evt.event_at.substring(0, 10)
                const endDate = evt.event_end_at.substring(0, 10)
                if (startDate === endDate) {
                    return start + ' - ' + evt.event_end_at.substring(11, 16)
                }
                const sp = startDate.split('-')
                const ep = endDate.split('-')
                return `${parseInt(sp[2])} ${MONTH_NAMES[parseInt(sp[1]) - 1].substring(0, 3)} ${start} - ${parseInt(ep[2])} ${MONTH_NAMES[parseInt(ep[1]) - 1].substring(0, 3)} ${evt.event_end_at.substring(11, 16)}`
            }
            return start
        },
        getEventsForHour(hour) {
            return this.selectedDayEvents.filter(evt => {
                if (!evt.event_end_at) {
                    return parseInt((evt.event_at || '').substring(11, 13), 10) === hour
                }
                const sd = (evt.event_at || '').substring(0, 10)
                const ed = evt.event_end_at.substring(0, 10)
                if (sd !== ed && sd < this.selectedDate && ed > this.selectedDate) return false
                if (ed === this.selectedDate && sd < this.selectedDate) {
                    return parseInt(evt.event_end_at.substring(11, 13), 10) === hour
                }
                return parseInt((evt.event_at || '').substring(11, 13), 10) === hour
            })
        },

        showTooltip(evt, e) {
            if (this.isCompact || 'ontouchstart' in window) return
            clearTimeout(this.tooltipTimer)
            this.tooltipTimer = setTimeout(() => {
                const el = e.target.closest('[data-evt-id]')
                if (!el) return
                const rect = el.getBoundingClientRect()
                this._tooltipEvtRect = rect
                this.tooltip = {
                    visible: true,
                    title: evt.title,
                    time: this.formatTooltipTime(evt),
                    description: evt.description || '',
                    color: evt.color || '#007AFF',
                    isShared: evt.user?.id !== this.currentUserId,
                    x: rect.left + rect.width / 2,
                    y: rect.top,
                }
                this.$nextTick(() => this.positionTooltip())
            }, 400)
        },

        hideTooltip() {
            clearTimeout(this.tooltipTimer)
            this.tooltipTimer = null
            this.tooltip.visible = false
        },

        positionTooltip() {
            const el = this.$refs.tooltipPanel
            if (!el) return
            const tr = el.getBoundingClientRect()
            const evtRect = this._tooltipEvtRect
            let x = this.tooltip.x
            let y = this.tooltip.y

            // Yukarida yer yoksa altinda goster
            if (y - tr.height - 8 < 0) {
                el.style.transform = 'translateX(-50%) translateY(8px)'
                y = evtRect ? evtRect.bottom : y + 30
            }

            // Yatayda viewport sinirlarina gore clamp et
            const pad = 8
            if (x - tr.width / 2 < pad) {
                x = pad + tr.width / 2
            } else if (x + tr.width / 2 > window.innerWidth - pad) {
                x = window.innerWidth - pad - tr.width / 2
            }

            if (x !== this.tooltip.x || y !== this.tooltip.y) {
                this.tooltip.x = x
                this.tooltip.y = y
            }
        },

        formatTooltipTime(evt) {
            if (!evt.event_at) return ''
            const time = evt.event_at.substring(11, 16)
            const date = evt.event_at.substring(0, 10).split('-')
            let result = `${date[2]}.${date[1]}.${date[0]} ${time}`
            if (evt.event_end_at) {
                const endTime = evt.event_end_at.substring(11, 16)
                const endDate = evt.event_end_at.substring(0, 10).split('-')
                if (evt.event_at.substring(0, 10) === evt.event_end_at.substring(0, 10)) {
                    result += ' - ' + endTime
                } else {
                    result += ` - ${endDate[2]}.${endDate[1]}.${endDate[0]} ${endTime}`
                }
            }
            if (evt.recurrence) {
                const freq = evt.recurrence.frequency?.label
                if (freq) result += ' (' + freq + ')'
            }
            return result
        },

        async loadCalendar() {
            const events = await this.$store.dispatch('module/agenda/getCalendar', {
                year: this.currentYear,
                month: this.currentMonth,
            })
            if (Array.isArray(events)) {
                this.calendarItems = events
            }
            if (!this._keepSidebarCache) {
                this._clearSidebarCache()
            }
            this._keepSidebarCache = false
        },
        async refreshCalendar() {
            if (this.sidebarCachedDate) {
                const cachedParts = this.sidebarCachedDate.split('-')
                const cachedYear = parseInt(cachedParts[0])
                const cachedMonth = parseInt(cachedParts[1])
                if (cachedYear !== this.currentYear || cachedMonth !== this.currentMonth) {
                    const [calendarEvents, sidebarEvents] = await Promise.all([
                        this.$store.dispatch('module/agenda/getCalendar', {year: this.currentYear, month: this.currentMonth}),
                        this.$store.dispatch('module/agenda/getCalendar', {year: cachedYear, month: cachedMonth}),
                    ])
                    if (Array.isArray(calendarEvents)) this.calendarItems = calendarEvents
                    if (Array.isArray(sidebarEvents)) {
                        this.sidebarCachedEvents = sidebarEvents.filter(item => (item.event_at || '').substring(0, 10) === this.sidebarCachedDate)
                    }
                    return
                }
            }
            await this.loadCalendar()
        },
        async loadOfficers() {
            await this.$handler.loadMembers()
        },

        navPrev() {
            if (this.viewMode === 'day') {
                this.changeDay(-1)
            } else {
                this.changeMonth(-1)
            }
        },
        navNext() {
            if (this.viewMode === 'day') {
                this.changeDay(1)
            } else {
                this.changeMonth(1)
            }
        },
        _snapshotSidebar() {
            if (!this.selectedDate) return
            if (this.sidebarCachedEvents && this.sidebarCachedDate === this.selectedDate) return
            this.sidebarCachedDate = this.selectedDate
            this.sidebarCachedEvents = this.calendarItems
                .filter(item => (item.event_at || '').substring(0, 10) === this.selectedDate)
        },
        _clearSidebarCache() {
            this.sidebarCachedEvents = null
            this.sidebarCachedDate = null
        },
        changeMonth(dir) {
            this._snapshotSidebar()
            this._keepSidebarCache = true
            const m = this.currentMonth + dir
            if (m < 1) { this.currentMonth = 12; this.currentYear-- }
            else if (m > 12) { this.currentMonth = 1; this.currentYear++ }
            else { this.currentMonth = m }
            if (this.sidebarCachedDate) {
                const cp = this.sidebarCachedDate.split('-')
                if (parseInt(cp[0]) === this.currentYear && parseInt(cp[1]) === this.currentMonth) {
                    this._keepSidebarCache = false
                }
            }
            this.loadCalendar()
        },
        changeDay(dir) {
            this._clearSidebarCache()
            const parts = this.selectedDate.split('-')
            const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]))
            d.setDate(d.getDate() + dir)
            const newYear = d.getFullYear()
            const newMonth = d.getMonth() + 1
            this.selectedDate = `${newYear}-${String(newMonth).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
            if (newYear !== this.currentYear || newMonth !== this.currentMonth) {
                this.currentYear = newYear
                this.currentMonth = newMonth
                this.loadCalendar()
            }
        },
        goToday() {
            this._clearSidebarCache()
            const now = new Date()
            this.currentYear = now.getFullYear()
            this.currentMonth = now.getMonth() + 1
            this.selectedDate = `${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
            this.loadCalendar()
        },
        goToDate(dateStr) {
            const d = dateStr?.substring(0, 10)
            if (!d || d.length !== 10) return
            const [y, m, day] = d.split('-').map(Number)
            if (!y || !m || !day) return
            this._clearSidebarCache()
            this.selectedDate = d
            if (y !== this.currentYear || m !== this.currentMonth) {
                this.currentYear = y
                this.currentMonth = m
                this.loadCalendar()
            }
        },
        setViewMode(mode) {
            this.viewMode = mode
            if (this.isCompact && this.sidebarOpen) {
                this.sidebarOpen = false
                this.sheetFullscreen = false
            }
        },
        toggleSidebarGrouped() {
            this.sidebarGrouped = !this.sidebarGrouped
            this.panelSaveLayout()
        },
        toggleShowShared() {
            this.showShared = !this.showShared
            this.panelSaveLayout()
        },

        onDayClick(date) {
            this._clearSidebarCache()
            if (this.selectedDate === date && this.isCompact) {
                this.sidebarOpen = !this.sidebarOpen
            } else {
                this.selectedDate = date
                if (this.isCompact) {
                    this.sheetHeight = 200
                    this.sheetFullscreen = false
                }
                if (!this.sidebarOpen) {
                    this.sidebarOpen = true
                }
            }
        },
        onDayDblClick(date) {
            this._clearSidebarCache()
            this.selectedDate = date
            this.$refs.eventForm?.openNew(date)
        },
        onHourDblClick(hour) {
            const hourStr = String(hour).padStart(2, '0')
            this.$refs.eventForm?.openNew(this.selectedDate + 'T' + hourStr + ':00')
        },
        onNewEvent(presetDate) {
            this.$refs.eventForm?.openNew(presetDate || this.selectedDate)
        },
        onNewEventForDate(date) {
            this.$refs.eventForm?.openNew(date)
        },

        getContrastColor(bg) {
            if (!bg) return '#fff'
            const hex = bg.replace('#', '')
            const r = parseInt(hex.substring(0, 2), 16)
            const g = parseInt(hex.substring(2, 4), 16)
            const b = parseInt(hex.substring(4, 6), 16)
            return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#1c1c1e' : '#ffffff'
        },
        isMiddleDay(evt) {
            if (!evt.event_end_at || !this.selectedDate) return false
            const sd = (evt.event_at || '').substring(0, 10)
            const ed = evt.event_end_at.substring(0, 10)
            return sd !== ed && sd < this.selectedDate && ed > this.selectedDate
        },
        onAllDayEventClick(evt) {
            if (this.isOwn(evt)) {
                this.$refs.eventForm?.openEdit(evt)
            } else {
                this.$refs.eventForm?.openView(evt)
            }
        },
        onEventClick(event) {
            if (this._longPressed) {
                this._longPressed = false
                return
            }
            const dateStr = (event.event_at || '').substring(0, 10)
            if (dateStr) this.selectedDate = dateStr
            if (dateStr && dateStr !== this.selectedDate) {
                this._clearSidebarCache()
                this.selectedDate = dateStr
            }
            if (this.isOwn(event)) {
                this.$refs.eventForm?.openEdit(event)
            } else {
                this.$refs.eventForm?.openView(event)
            }
        },
        onEventContextMenu(mouseEvent, agendaEvent) {
            if (this._touchDragPending || this._touchDragActive || this._touchDragSource) return
            if (agendaEvent.user?.id !== this.currentUserId) return
            this.$refs.contextMenu?.openForEvent(mouseEvent, agendaEvent)
        },

        async onEventDrop({eventId, newDate}) {
            const event = this.calendarItems.find(e => e.id === eventId)
                || (this.sidebarCachedEvents && this.sidebarCachedEvents.find(e => e.id === eventId))
            if (!event) return
            if (event.user?.id !== this.currentUserId) return
            const currentDate = (event.event_at || '').substring(0, 10)
            if (currentDate === newDate) return
            const oldTime = (event.event_at || '').substring(11, 19) || '09:00:00'
            const newEventAt = newDate + ' ' + oldTime
            const updateData = {event_at: newEventAt}
            if (event.event_end_at) {
                const dayDiff = (new Date(newDate) - new Date(currentDate)) / 86400000
                const endDate = new Date(event.event_end_at.replace(' ', 'T'))
                endDate.setDate(endDate.getDate() + dayDiff)
                const y = endDate.getFullYear()
                const m = String(endDate.getMonth() + 1).padStart(2, '0')
                const d = String(endDate.getDate()).padStart(2, '0')
                const hh = String(endDate.getHours()).padStart(2, '0')
                const mm = String(endDate.getMinutes()).padStart(2, '0')
                updateData.event_end_at = `${y}-${m}-${d} ${hh}:${mm}`
            }
            if (event.reminders && event.reminders.length > 0) {
                const newEventDate = new Date(newEventAt.replace(' ', 'T'))
                const filtered = event.reminders.filter(r => {
                    if (r.type === 'relative' || r.minutes_before != null) return true
                    if (r.remind_at) {
                        const remDate = new Date(r.remind_at.replace(' ', 'T'))
                        return remDate < newEventDate
                    }
                    return true
                }).map(r => {
                    if (r.minutes_before != null) return {minutes_before: r.minutes_before}
                    return {remind_at: r.remind_at}
                })
                if (filtered.length !== event.reminders.length) {
                    updateData.reminders = filtered
                }
            }
            if (event.recurrence) {
                this._askRecurrenceScope(event, async (scope, realign) => {
                    updateData.update_scope = scope
                    if (realign) updateData.realign = true
                    await this.$store.dispatch('module/agenda/update', {id: eventId, formData: updateData})
                    await this.refreshCalendar()
                }, {dateChanged: true})
            } else {
                await this.$store.dispatch('module/agenda/update', {id: eventId, formData: updateData})
                await this.refreshCalendar()
            }
        },

        onMainDragStart(e) {
            this.hideTooltip()
            if (this._touchDragSource === 'cell') e.preventDefault()
        },
        onMainPointerDown(e) {
            if (e.pointerType === 'mouse') return
            const eventEl = e.target.closest('.agenda-day-event, .agenda-day-hour-event')
            if (!eventEl) return
            const evtId = eventEl.dataset.evtId
            if (!evtId) return
            const evt = this.calendarItems.find(ev => ev.id === parseInt(evtId, 10))
            if (!evt || evt.user?.id !== this.currentUserId) return
            clearTimeout(this._dragLongPressTimer)
            this._touchDragActive = false
            this._touchDragPending = false
            this._touchDragSource = 'cell'
            this._swipeCardEl = eventEl
            this.touchDraggingBlockId = evt.id

            this._dragOriginX = e.clientX
            this._dragOriginY = e.clientY

            if (eventEl.hasAttribute('draggable')) {
                eventEl.removeAttribute('draggable')
                this._touchDragEl = eventEl
            }

            document.addEventListener('touchmove', this.onTouchDragMove, {passive: false})
            document.addEventListener('touchend', this.onTouchDragEnd)
            document.addEventListener('touchcancel', this.onTouchDragEnd)

            this._dragLongPressTimer = setTimeout(() => {
                this._touchDragPending = true
                this._touchDragEvt = evt
                this.touchDragReadyId = evt.id
                this._vibrate(15)
                clearTimeout(this._longPressTimer)
                this._longPressTimer = null
                this._longPressReady = false
                this._lockScrollParents(eventEl)
            }, 350)
        },

        onDayEventDragStart(e, evt) {
            e.dataTransfer.setData('text/agenda-event-id', String(evt.id))
            e.dataTransfer.effectAllowed = 'move'
            const ghost = document.createElement('div')
            ghost.textContent = evt.title
            ghost.style.cssText = `position:fixed; left:-9999px; top:-9999px; padding:5px 10px; background:var(--panel-bg, #fff); color:var(--panel-text-primary, #1c1c1e); border-left:3px solid ${evt.color || '#007AFF'}; border-radius:4px; font-size:12px; font-weight:500; max-width:160px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif; box-shadow:0 2px 8px rgba(0,0,0,0.15);`
            document.body.appendChild(ghost)
            e.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, ghost.offsetHeight + 10)
            requestAnimationFrame(() => ghost.remove())
        },
        onDayHourDragOver(e, hour) {
            this.dayDragOverHour = hour
            e.dataTransfer.dropEffect = 'move'
        },
        onDayHourDragLeave(hour) {
            if (this.dayDragOverHour === hour) this.dayDragOverHour = null
        },
        async onDayHourDrop(e, hour) {
            this.dayDragOverHour = null
            const eventId = e.dataTransfer.getData('text/agenda-event-id')
            if (!eventId) return
            const event = this.calendarItems.find(ev => ev.id === parseInt(eventId, 10))
                || (this.sidebarCachedEvents && this.sidebarCachedEvents.find(ev => ev.id === parseInt(eventId, 10)))
            if (!event || event.user?.id !== this.currentUserId) return
            const hourStr = String(hour).padStart(2, '0')
            const sd = (event.event_at || '').substring(0, 10)
            const ed = event.event_end_at ? event.event_end_at.substring(0, 10) : null
            const isEndDay = ed && ed === this.selectedDate && sd < this.selectedDate
            let updateData
            if (isEndDay) {
                const newEndAt = ed + ' ' + hourStr + ':00'
                if (event.event_end_at.startsWith(newEndAt)) return
                updateData = {event_end_at: newEndAt}
            } else {
                const newEventAt = sd + ' ' + hourStr + ':00'
                if ((event.event_at || '').startsWith(newEventAt)) return
                updateData = {event_at: newEventAt}
                if (event.event_end_at && sd === ed) {
                    const oldStart = new Date(event.event_at.replace(' ', 'T'))
                    const oldEnd = new Date(event.event_end_at.replace(' ', 'T'))
                    const duration = oldEnd - oldStart
                    const newEnd = new Date(new Date(newEventAt.replace(' ', 'T')).getTime() + duration)
                    const y = newEnd.getFullYear()
                    const m = String(newEnd.getMonth() + 1).padStart(2, '0')
                    const d = String(newEnd.getDate()).padStart(2, '0')
                    const hh = String(newEnd.getHours()).padStart(2, '0')
                    const mm = String(newEnd.getMinutes()).padStart(2, '0')
                    updateData.event_end_at = `${y}-${m}-${d} ${hh}:${mm}`
                }
            }
            if (!isEndDay && event.reminders && event.reminders.length > 0) {
                const newEventDate = new Date(updateData.event_at.replace(' ', 'T'))
                const filtered = event.reminders.filter(r => {
                    if (r.type === 'relative' || r.minutes_before != null) return true
                    if (r.remind_at) return new Date(r.remind_at.replace(' ', 'T')) < newEventDate
                    return true
                }).map(r => r.minutes_before != null ? {minutes_before: r.minutes_before} : {remind_at: r.remind_at})
                if (filtered.length !== event.reminders.length) updateData.reminders = filtered
            }
            if (event.recurrence) {
                this._askRecurrenceScope(event, async (scope) => {
                    updateData.update_scope = scope
                    await this.$store.dispatch('module/agenda/update', {id: event.id, formData: updateData})
                    await this.refreshCalendar()
                })
            } else {
                await this.$store.dispatch('module/agenda/update', {id: event.id, formData: updateData})
                await this.refreshCalendar()
            }
        },

        onSidebarDragStart(e, evt) {
            if (this._isSwipeGesture) {
                e.preventDefault()
                return
            }
            if (this._swipeStartX != null && evt.user?.id === this.currentUserId) {
                const dx = e.clientX - this._swipeStartX
                const dy = e.clientY - this._swipeStartY
                if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) >= 2) {
                    e.preventDefault()
                    this._swipeLocked = true
                    this._isSwipeGesture = true
                    this._swiped = true
                    this._swipingEvt = this._swipingEvt || evt
                    clearTimeout(this._dragLongPressTimer)
                    this._dragLongPressTimer = null
                    clearTimeout(this._longPressTimer)
                    this._longPressReady = false
                    this.swipingEventId = this._swipingEvt.id
                    document.addEventListener('mousemove', this._onDocMouseMove)
                    document.addEventListener('mouseup', this._onDocMouseUp)
                    const maxSwipe = 120
                    this.swipeX = Math.max(-maxSwipe, Math.min(maxSwipe, dx))
                    this._checkSwipeThreshold()
                    return
                }
            }
            this._swipeStartX = null
            this._swipeLocked = false
            this._isSwipeGesture = false
            this.swipingEventId = null
            this.swipeX = 0

            if (evt.user?.id !== this.currentUserId) {
                e.preventDefault()
                return
            }
            this._sidebarDragActive = true
            e.dataTransfer.setData('text/agenda-event-id', String(evt.id))
            e.dataTransfer.effectAllowed = 'move'
        },
        onSidebarDragEnd() {
            this._sidebarDragActive = false
            this.dragReadyEventId = null
        },
        onSidebarDragOver(e) {
            if (this._sidebarDragActive) return
            e.preventDefault()
            this.sidebarDragOver = true
        },
        onSidebarDrop(e) {
            this.sidebarDragOver = false
            const eventId = e.dataTransfer.getData('text/agenda-event-id')
            if (eventId) {
                this.onEventDrop({eventId: parseInt(eventId, 10), newDate: this.selectedDate})
            }
        },

        startTouchDrag(evt, x, y) {
            this._swiped = true
            this._touchDragActive = true
            this._touchDragPending = false
            this.touchDragReadyId = null

            const sourceCard = this._swipeCardEl
            const w = sourceCard ? sourceCard.offsetWidth : 200
            const ghost = sourceCard ? sourceCard.cloneNode(true) : document.createElement('div')
            if (sourceCard) {
                const cs = getComputedStyle(sourceCard)
                ghost.style.background = cs.backgroundColor
            }
            ghost.style.position = 'fixed'
            ghost.style.zIndex = '200010'
            ghost.style.pointerEvents = 'none'
            ghost.style.opacity = '0.9'
            ghost.style.width = w + 'px'
            ghost.style.willChange = 'transform'
            ghost.style.transition = 'none'
            ghost.style.transform = `translate(${x - w / 2}px, ${y - 20}px)`
            this.$el.appendChild(ghost)
            this._dragGhost = ghost
            this._dragGhostW = w

            if (this.isCompact && this.sidebarOpen && this._touchDragSource !== 'cell') {
                this._dragClosedSheet = true
                this._dragWasFullscreen = this.sheetFullscreen
                this.sheetFullscreen = false
                this.sheetClosing = true
                this.sheetHeight = 0
            }

            document.body.style.userSelect = 'none'
            document.body.style.webkitUserSelect = 'none'
            this._dragEvt = evt
            this._dragHighlightCell = null
        },
        onTouchDragMove(e) {
            if (this._dragLongPressTimer && !this._touchDragPending && !this._touchDragActive) {
                const touch = e.touches[0]
                const dx = Math.abs(touch.clientX - this._dragOriginX)
                const dy = Math.abs(touch.clientY - this._dragOriginY)
                if (dx > 5 || dy > 5) {
                    this.cleanupTouchDrag()
                }
                return
            }
            if (this._touchDragPending) {
                const touch = e.touches[0]
                const dx = Math.abs(touch.clientX - this._dragOriginX)
                const dy = Math.abs(touch.clientY - this._dragOriginY)
                if (dx < 10 && dy < 10) {
                    e.preventDefault()
                    return
                }
                this.startTouchDrag(this._touchDragEvt, touch.clientX, touch.clientY)
                e.preventDefault()
                return
            }
            if (!this._touchDragActive) {
                if (this._swipeLocked) e.preventDefault()
                return
            }
            e.preventDefault()
            const touch = e.touches[0]
            if (this._dragGhost) {
                const w = this._dragGhostW || this._dragGhost.offsetWidth
                this._dragGhost.style.transform = `translate(${touch.clientX - w / 2}px, ${touch.clientY - 20}px)`
            }
            const ghost = this._dragGhost
            if (ghost) ghost.style.display = 'none'
            const el = document.elementFromPoint(touch.clientX, touch.clientY)
            if (ghost) ghost.style.display = ''
            const target = el?.closest('.agenda-day-cell') || el?.closest('.agenda-day-hour-row')
            const sidebarTarget = !target && this._touchDragSource === 'cell' ? el?.closest('.agenda-app-sidebar') : null
            this.sidebarDragOver = !!sidebarTarget
            const highlightClass = target?.classList.contains('agenda-day-hour-row') ? 'agenda-day-hour-row--drag-over' : 'agenda-day-cell--drag-over'
            if (target !== this._dragHighlightCell) {
                if (this._dragHighlightCell) {
                    this._dragHighlightCell.classList.remove('agenda-day-cell--drag-over')
                    this._dragHighlightCell.classList.remove('agenda-day-hour-row--drag-over')
                }
                if (target) target.classList.add(highlightClass)
                this._dragHighlightCell = target
            }
            this._lastTouchX = touch.clientX
            this._lastTouchY = touch.clientY
            this._autoScrollDuringDrag(touch.clientY)
        },
        onTouchDragEnd(e) {
            if (this._touchDragActive) {
                if (this._dragGhost) { this._dragGhost.remove(); this._dragGhost = null }

                const touch = e.changedTouches?.[0]
                const tx = touch ? touch.clientX : this._lastTouchX
                const ty = touch ? touch.clientY : this._lastTouchY

                let cell = null, hourRow = null, sidebar = null
                if (tx != null && ty != null) {
                    const el = document.elementFromPoint(tx, ty)
                    cell = el?.closest('.agenda-day-cell')
                    hourRow = el?.closest('.agenda-day-hour-row')
                    if (!cell && !hourRow && this._touchDragSource === 'cell') sidebar = el?.closest('.agenda-app-sidebar')
                }

                if (sidebar && this._dragEvt) {
                    this.sidebarDragOver = false
                    this.onEventDrop({eventId: this._dragEvt.id, newDate: this.selectedDate})
                    this.cleanupTouchDrag()
                    return
                }

                if (!cell && !hourRow && this._dragHighlightCell) {
                    if (this._dragHighlightCell.classList.contains('agenda-day-hour-row')) {
                        hourRow = this._dragHighlightCell
                    } else {
                        cell = this._dragHighlightCell
                    }
                }

                if (cell && this._dragEvt) {
                    const newDate = cell.dataset.date
                    if (newDate) this.onEventDrop({eventId: this._dragEvt.id, newDate})
                } else if (hourRow && this._dragEvt) {
                    const hour = parseInt(hourRow.dataset.hour, 10)
                    if (!isNaN(hour)) {
                        const fakeE = {dataTransfer: {getData: () => String(this._dragEvt.id)}}
                        this.onDayHourDrop(fakeE, hour)
                    }
                }
                this.cleanupTouchDrag()
                return
            }

            if (this._touchDragPending) {
                this._swiped = true
                this._longPressed = true
                const evt = this._touchDragEvt
                const touch = e.changedTouches?.[0]
                this.cleanupTouchDrag()
                if (evt && evt.user?.id === this.currentUserId && touch) {
                    this.onEventContextMenu(
                        {clientX: touch.clientX, clientY: touch.clientY, preventDefault() {}},
                        evt
                    )
                }
                return
            }

            this.cleanupTouchDrag()
        },
        _lockScrollParents(el) {
            this._lockedScrollEls = []
            let node = el?.parentElement
            while (node && node !== this.$el) {
                const cs = getComputedStyle(node)
                if (cs.overflowY === 'auto' || cs.overflowY === 'scroll') {
                    this._lockedScrollEls.push({el: node, val: node.style.overflowY})
                    node.style.overflowY = 'hidden'
                }
                node = node.parentElement
            }
        },
        _unlockScrollParents() {
            if (this._lockedScrollEls) {
                for (const item of this._lockedScrollEls) {
                    item.el.style.overflowY = item.val
                }
                this._lockedScrollEls = null
            }
        },
        _autoScrollDuringDrag(touchY) {
            if (!this._lockedScrollEls?.length) return
            const scrollEl = this._lockedScrollEls[0].el
            const rect = scrollEl.getBoundingClientRect()
            const edge = 40
            const maxSpeed = 8
            let speed = 0
            if (touchY < rect.top + edge && scrollEl.scrollTop > 0) {
                speed = -Math.round(maxSpeed * Math.max(0, 1 - (touchY - rect.top) / edge))
            } else if (touchY > rect.bottom - edge && scrollEl.scrollTop < scrollEl.scrollHeight - scrollEl.clientHeight) {
                speed = Math.round(maxSpeed * Math.max(0, 1 - (rect.bottom - touchY) / edge))
            }
            if (speed) {
                this._autoScrollSpeed = speed
                if (!this._autoScrollRAF) this._runAutoScroll()
            } else {
                this._stopAutoScroll()
            }
        },
        _runAutoScroll() {
            if (!this._autoScrollSpeed || !this._lockedScrollEls?.length) {
                this._autoScrollRAF = null
                return
            }
            this._lockedScrollEls[0].el.scrollTop += this._autoScrollSpeed
            this._autoScrollRAF = requestAnimationFrame(() => this._runAutoScroll())
        },
        _stopAutoScroll() {
            this._autoScrollSpeed = 0
            if (this._autoScrollRAF) {
                cancelAnimationFrame(this._autoScrollRAF)
                this._autoScrollRAF = null
            }
        },
        cleanupTouchDrag() {
            this._stopAutoScroll()
            clearTimeout(this._dragLongPressTimer)
            this._dragLongPressTimer = null
            this._touchDragActive = false
            this._touchDragPending = false
            this._touchDragSource = null
            this.touchDraggingBlockId = null
            this.touchDragReadyId = null
            clearTimeout(this._longPressTimer)
            this._longPressTimer = null
            this._longPressReady = false
            this._unlockScrollParents()
            if (this._touchDragEl) {
                this._touchDragEl.setAttribute('draggable', 'true')
                this._touchDragEl = null
            }
            if (this._dragHighlightCell) {
                this._dragHighlightCell.classList.remove('agenda-day-cell--drag-over')
                this._dragHighlightCell.classList.remove('agenda-day-hour-row--drag-over')
                this._dragHighlightCell = null
            }
            this.sidebarDragOver = false
            if (this._dragGhost) { this._dragGhost.remove(); this._dragGhost = null }
            this._dragEvt = null
            document.body.style.userSelect = ''
            document.body.style.webkitUserSelect = ''
            document.removeEventListener('touchmove', this.onTouchDragMove)
            document.removeEventListener('touchend', this.onTouchDragEnd)
            document.removeEventListener('touchcancel', this.onTouchDragEnd)
            if (this._dragClosedSheet) {
                this._dragClosedSheet = false
                this.sidebarOpen = false
                this.sheetClosing = false
                this.sheetFullscreen = this._dragWasFullscreen || false
                this.sheetHeight = 200
            }
        },

        onSwipeStart(e, evt) {
            if (e.button && e.button !== 0) return
            this._swipeStartX = e.clientX
            this._swipeStartY = e.clientY
            this._swipeLocked = false
            this._swiped = false
            this._swipingEvt = evt
            this._swipeCardEl = e.target.closest('.agenda-sidebar-event')
            this._isSwipeGesture = false
            this._swipePointerType = e.pointerType
            this._swipeOverThreshold = false
            this.dragReadyEventId = null
            this.touchDraggingBlockId = null
            this._touchDragActive = false
            this._touchDragPending = false

            clearTimeout(this._dragLongPressTimer)
            if (evt.user?.id === this.currentUserId) {
                if (e.pointerType !== 'mouse') {
                    this._touchDragSource = 'sidebar'
                    this._dragOriginX = e.clientX
                    this._dragOriginY = e.clientY
                    this.touchDraggingBlockId = evt.id
                    document.addEventListener('touchmove', this.onTouchDragMove, {passive: false})
                    document.addEventListener('touchend', this.onTouchDragEnd)
                    document.addEventListener('touchcancel', this.onTouchDragEnd)

                    this._dragLongPressTimer = setTimeout(() => {
                        this._swipeStartX = null
                        this._swipeLocked = false
                        this.swipingEventId = null
                        this.swipeX = 0
                        clearTimeout(this._longPressTimer)
                        this._longPressReady = false
                        this._touchDragPending = true
                        this._touchDragEvt = evt
                        this.touchDragReadyId = evt.id
                    }, 300)
                }
            }
        },
        onSwipeMove(e) {
            if (this._touchDragPending) {
                const dx = Math.abs(e.clientX - this._dragOriginX)
                const dy = Math.abs(e.clientY - this._dragOriginY)
                if (dx < 10 && dy < 10) return
                this.startTouchDrag(this._touchDragEvt, e.clientX, e.clientY)
                return
            }
            if (this._touchDragActive) return
            if (this._swipeStartX == null) return
            const dx = e.clientX - this._swipeStartX
            const dy = e.clientY - this._swipeStartY

            if (!this._swipeLocked) {
                if (Math.abs(dx) < 3 && Math.abs(dy) < 3) return
                if (this._swipePointerType === 'mouse' ? Math.abs(dy) > Math.abs(dx) * 1.5 : Math.abs(dy) >= Math.abs(dx)) {
                    return
                }
                this._swipeLocked = true
                this._isSwipeGesture = true
                this._swiped = true
                this.swipingEventId = this._swipingEvt?.id ?? null
                clearTimeout(this._dragLongPressTimer)
                this._dragLongPressTimer = null
                clearTimeout(this._longPressTimer)
                this._longPressReady = false
                if (this._swipePointerType === 'mouse') {
                    document.addEventListener('mousemove', this._onDocMouseMove)
                    document.addEventListener('mouseup', this._onDocMouseUp)
                } else {
                    document.addEventListener('pointermove', this.onDocSwipeMove)
                    document.addEventListener('pointerup', this.onDocSwipeUp)
                }
            }

            if (this._swipePointerType !== 'mouse') e.preventDefault()
            const maxSwipe = 120
            this.swipeX = Math.max(-maxSwipe, Math.min(maxSwipe, dx))
            this._checkSwipeThreshold()
        },
        onDocSwipeMove(e) {
            if (!this._swipeLocked || this._swipeStartX == null) return
            const dx = e.clientX - this._swipeStartX
            const maxSwipe = 120
            this.swipeX = Math.max(-maxSwipe, Math.min(maxSwipe, dx))
            this._checkSwipeThreshold()
        },
        onDocSwipeUp() {
            document.removeEventListener('pointermove', this.onDocSwipeMove)
            document.removeEventListener('pointerup', this.onDocSwipeUp)
            if (this._swipeLocked && this._swipingEvt) {
                this._resolveSwipe(this._swipingEvt)
            }
            this._swipeStartX = null
            this._swipeLocked = false
            this._isSwipeGesture = false
        },
        _onDocMouseMove(e) {
            if (!this._swipeLocked || this._swipeStartX == null) return
            const dx = e.clientX - this._swipeStartX
            const maxSwipe = 120
            this.swipeX = Math.max(-maxSwipe, Math.min(maxSwipe, dx))
            this._checkSwipeThreshold()
        },
        _onDocMouseUp() {
            document.removeEventListener('mousemove', this._onDocMouseMove)
            document.removeEventListener('mouseup', this._onDocMouseUp)
            if (this._swipeLocked && this._swipingEvt) {
                this._resolveSwipe(this._swipingEvt)
            }
            this._swipeStartX = null
            this._swipeLocked = false
            this._isSwipeGesture = false
        },
        onSwipeEnd(e, evt) {
            clearTimeout(this._dragLongPressTimer)
            this._dragLongPressTimer = null
            if (this._touchDragActive || this._touchDragPending) return
            if (this.touchDraggingBlockId) {
                this.touchDraggingBlockId = null
                this.cleanupTouchDrag()
            }
            if (this._swipeStartX == null && !this._swipeLocked) return
            if (this._swipePointerType === 'mouse' && this._swipeLocked) return
            if (!this._swipeLocked) {
                this._swipeStartX = null
                this.swipingEventId = null
                return
            }
            document.removeEventListener('pointermove', this.onDocSwipeMove)
            document.removeEventListener('pointerup', this.onDocSwipeUp)
            this._resolveSwipe(evt)
            this._swipeStartX = null
            this._swipeLocked = false
            this._isSwipeGesture = false
        },
        onSwipeCancel() {
            if (this._touchDragActive || this._touchDragPending) return
            clearTimeout(this._dragLongPressTimer)
            this._dragLongPressTimer = null
            if (this._isSwipeGesture) return
            this.swipeX = 0
        },

        _vibrate(ms) {
            if (navigator.vibrate) navigator.vibrate(ms)
        },
        _checkSwipeThreshold() {
            const isOver = Math.abs(this.swipeX) >= 60
            if (isOver !== this._swipeOverThreshold) {
                this._vibrate(10)
                this._swipeOverThreshold = isOver
            }
        },
        _resolveSwipe(evt) {
            const dx = this.swipeX
            const threshold = 60

            if (Math.abs(dx) > threshold && dx > 0) {
                this.swipeX = 0
                this.swipingEventId = null
                this.onStatusToggle({id: evt.id, status_id: evt.status?.id === 2 ? 1 : 2})
            } else if (Math.abs(dx) > threshold && dx < 0) {
                this.swipeX = 0
                this.swipingEventId = null
                this.onSwipeDelete(evt)
            } else {
                this.swipeX = 0
                this.swipingEventId = null
            }
        },
        onSwipeEventClick(evt) {
            if (this._swiped) {
                this._swiped = false
                return
            }
            if (this._longPressed) {
                this._longPressed = false
                return
            }
            this.onEventClick(evt)
        },
        async onSwipeDelete(evt) {
            this._showDeleteDialog(evt)
        },

        onContextEdit(event) { this.$refs.eventForm?.openEdit(event) },
        onContextReminder(event) { this.$refs.reminderPicker?.open(event) },
        async onContextRemoveReminder(event) {
            if (event.recurrence) {
                this._askRecurrenceScope(event, (scope) => {
                    this.$store.dispatch('module/agenda/update', {id: event.id, formData: {reminders: [], update_scope: scope}}).then(() => this.refreshCalendar())
                })
            } else {
                await this.$store.dispatch('module/agenda/update', {id: event.id, formData: {reminders: []}})
                await this.refreshCalendar()
            }
        },
        async onStatusToggle({id, status_id}) {
            await this.$store.dispatch('module/agenda/updateStatus', {id, status_id})
            await this.refreshCalendar()
        },
        async onContextDelete(event) {
            this._showDeleteDialog(event)
        },

        _askRecurrenceScope(event, onConfirm, {dateChanged} = {}) {
            const rec = event.recurrence
            if (rec.occurrence_count <= 1) { onConfirm('this_only'); return }
            const buttons = [
                {label: this.$t('pages.module.AGENDA.scope.thisOnly'), type: 'primary', value: 'this_only'},
            ]
            if (rec.has_future && rec.has_past) buttons.push({label: this.$t('pages.module.AGENDA.scope.thisAndFuture'), type: 'default', value: 'this_and_future'})
            buttons.push(
                {label: this.$t('pages.module.AGENDA.scope.all'), type: 'danger', value: 'all'},
                {label: this.$t('common.cancel'), type: 'cancel', value: false},
            )
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('pages.module.AGENDA.scope.updateTitle'),
                message: this.$t('pages.module.AGENDA.scope.updateMessage'),
                buttons,
                onConfirm: (val) => {
                    if (!val) return
                    if (val !== 'this_only' && dateChanged) {
                        this.$nextTick(() => this._askRealign((realign) => onConfirm(val, realign)))
                    } else {
                        onConfirm(val)
                    }
                },
            })
        },
        _askRealign(onConfirm) {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('pages.module.AGENDA.scope.realignTitle'),
                message: this.$t('pages.module.AGENDA.scope.realignMessage'),
                buttons: [
                    {label: this.$t('pages.module.AGENDA.scope.realignShift'), type: 'primary', value: false},
                    {label: this.$t('pages.module.AGENDA.scope.realignAlign'), type: 'danger', value: true},
                    {label: this.$t('common.cancel'), type: 'cancel', value: null},
                ],
                onConfirm: (val) => {
                    if (val === null) return
                    onConfirm(val)
                },
            })
        },
        _showDeleteDialog(event) {
            const rec = event.recurrence
            const showScope = rec && rec.occurrence_count > 1
            if (showScope) {
                const buttons = [
                    {label: this.$t('pages.module.AGENDA.scope.thisOnly'), type: 'primary', value: 'this_only'},
                ]
                if (rec.has_future && rec.has_past) buttons.push({label: this.$t('pages.module.AGENDA.scope.thisAndFuture'), type: 'default', value: 'this_and_future'})
                buttons.push(
                    {label: this.$t('pages.module.AGENDA.scope.all'), type: 'danger', value: 'all'},
                    {label: this.$t('common.cancel'), type: 'cancel', value: false},
                )
                this.eventBus.$emit('showConfirmDialog', {
                    title: this.$t('pages.module.AGENDA.scope.deleteTitle'),
                    message: this.$t('pages.module.AGENDA.scope.deleteMessage'),
                    buttons,
                    onConfirm: async (deleteScope) => {
                        if (!deleteScope) return
                        await this.$store.dispatch('module/agenda/delete', {id: event.id, deleteScope})
                        await this.refreshCalendar()
                    },
                })
            } else {
                this.eventBus.$emit('showConfirmDialog', {
                    title: this.$t('pages.module.AGENDA.contextMenu.deleteTitle'),
                    message: this.$t('pages.module.AGENDA.contextMenu.deleteMessage'),
                    confirmText: this.$t('common.delete'),
                    cancelText: this.$t('common.cancel'),
                    confirmType: 'danger',
                    onConfirm: async () => {
                        await this.$store.dispatch('module/agenda/delete', {id: event.id})
                        await this.refreshCalendar()
                    },
                })
            }
        },

        async onEventSave({id, formData}) {
            let result
            if (id) { result = await this.$store.dispatch('module/agenda/update', {id, formData}) }
            else { result = await this.$store.dispatch('module/agenda/add', {formData}) }
            if (result && result.status) {
                this.$refs.eventForm?.onSaveComplete()
                await this.refreshCalendar()
            } else {
                this.$refs.eventForm?.onSaveError()
            }
        },
        async onReminderSave({event, reminders}) {
            if (event.recurrence) {
                this._askRecurrenceScope(event, (scope) => {
                    this.$store.dispatch('module/agenda/update', {id: event.id, formData: {reminders, update_scope: scope}}).then(() => this.refreshCalendar())
                })
            } else {
                await this.$store.dispatch('module/agenda/update', {id: event.id, formData: {reminders}})
                await this.refreshCalendar()
            }
        },

        getBodyHeight() {
            return this.$refs.agendaBody?.clientHeight || this.windowHeight
        },
        onSheetDragStart(e) {
            if (!this.isCompact) return
            const touch = e.touches[0]
            const bodyH = this.getBodyHeight()
            this.sheetDragStartY = touch.clientY
            this.sheetDragStartH = this.sheetFullscreen ? bodyH : this.sheetHeight
            if (this.sheetFullscreen) {
                this.sheetHeight = bodyH
            }
            this.sheetFullscreen = false
            this.sheetDragging = true
            this._sheetLastY = touch.clientY
            this._sheetLastT = Date.now()
            this._sheetVelocity = 0
        },
        onSheetDragMove(e) {
            if (!this.sheetDragging) return
            const touch = e.touches[0]
            const now = Date.now()
            const dt = now - this._sheetLastT
            if (dt > 0) {
                this._sheetVelocity = (this._sheetLastY - touch.clientY) / dt
            }
            this._sheetLastY = touch.clientY
            this._sheetLastT = now
            const dy = this.sheetDragStartY - touch.clientY
            let newH = this.sheetDragStartH + dy
            const maxH = this.getBodyHeight()
            const minH = 60
            newH = Math.max(minH, Math.min(newH, maxH))
            this.sheetHeight = newH
        },
        onSheetDragEnd() {
            if (!this.sheetDragging) return
            this.sheetDragging = false
            const maxH = this.getBodyHeight()
            const v = this._sheetVelocity || 0
            if (v > 0.5) {
                this.sheetFullscreen = true
            } else if (v < -0.5) {
                this.closeSheet()
            } else if (this.sheetHeight > maxH * 0.85) {
                this.sheetFullscreen = true
            } else if (this.sheetHeight < 100) {
                this.closeSheet()
            }
        },
        closeSheet() {
            this.sheetClosing = true
            this.sheetHeight = 0
            setTimeout(() => {
                this.sidebarOpen = false
                this.sheetClosing = false
                this.sheetHeight = 200
            }, 250)
        },

        onLongPressStart(e, evt) {
            const touch = e.touches[0]
            this._lpStartX = touch.clientX
            this._lpStartY = touch.clientY
            this._longPressed = false
            this._longPressReady = false
            this._lpEvent = evt
            this._longPressTimer = setTimeout(() => {
                this._longPressReady = true
                this._longPressTimer = null
                this._vibrate(15)
            }, 350)
        },
        onLongPressMove(e) {
            if (!this._longPressTimer && !this._longPressReady) return
            const touch = e.touches[0]
            const dx = Math.abs(touch.clientX - this._lpStartX)
            const dy = Math.abs(touch.clientY - this._lpStartY)
            if (dx > 20 || dy > 20) {
                clearTimeout(this._longPressTimer)
                this._longPressTimer = null
                this._longPressReady = false
            }
        },
        onLongPressEnd(e) {
            clearTimeout(this._longPressTimer)
            this._longPressTimer = null
            if (this._longPressReady) {
                const touch = e.changedTouches[0]
                const dx = Math.abs(touch.clientX - this._lpStartX)
                const dy = Math.abs(touch.clientY - this._lpStartY)
                if (dx < 40 && dy < 40) {
                    this._longPressReady = false
                    this._longPressed = true
                    this.onEventContextMenu(
                        {clientX: this._lpStartX, clientY: this._lpStartY, preventDefault() {}},
                        this._lpEvent
                    )
                    return
                }
            }
            this._longPressReady = false
        },
    },
    beforeUnmount() {
        this._contextUnwatch?.()
        clearTimeout(this.tooltipTimer)
        clearTimeout(this._longPressTimer)
        clearTimeout(this._dragLongPressTimer)
        document.removeEventListener('pointermove', this.onDocSwipeMove)
        document.removeEventListener('pointerup', this.onDocSwipeUp)
        document.removeEventListener('mousemove', this._onDocMouseMove)
        document.removeEventListener('mouseup', this._onDocMouseUp)
        this.cleanupTouchDrag()
    },
}
</script>

<style>
@import '../panel/panel-base.css';

.agenda-app {
    --agenda-sidebar-bg: #f9f9fb;
    --agenda-item-hover: #f5f5f5;
    --agenda-cell-border: #f0f0f2;

    -webkit-user-select: none;
    user-select: none;
}

html.dark .agenda-app {
    --agenda-sidebar-bg: #1B1C22;
    --agenda-item-hover: #1E2027;
    --agenda-cell-border: #1E2027;
}

html.midnight .agenda-app {
    --agenda-sidebar-bg: #201D19;
    --agenda-item-hover: #241F1B;
    --agenda-cell-border: #241F1B;
}

html.cream .agenda-app {
    --agenda-sidebar-bg: #F5F1EA;
    --agenda-item-hover: #F0EBE3;
    --agenda-cell-border: #E8E3DA;
}

.agenda-app .panel-titlebar { gap: 8px; position: relative; min-height: 45px; box-sizing: border-box; }
.agenda-app .panel-titlebar-left { flex-shrink: 0; z-index: 1; }
.agenda-app-titlebar-center { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); display: flex; align-items: center; justify-content: center; }
.agenda-titlebar-center-left { display: flex; align-items: center; gap: 6px; }
.agenda-titlebar-center-right { display: none; }
.agenda-titlebar-shared-label { font-size: 11px; color: var(--panel-text-secondary); white-space: nowrap; }
.agenda-app .panel-titlebar-actions { flex-shrink: 0; margin-left: auto; z-index: 1; }

.agenda-nav-group { display: flex; align-items: center; gap: 2px; background: var(--panel-bg); border: 1px solid var(--panel-border); border-radius: 7px; padding: 1px 2px; }
.agenda-nav-label { font-size: 12px; font-weight: 600; color: var(--panel-text-primary); padding: 0 4px; white-space: nowrap; width: 120px; text-align: center; }
.agenda-nav-btn { display: flex; align-items: center; justify-content: center; width: 24px; height: 24px; border: none; background: none; border-radius: 5px; cursor: pointer; color: var(--panel-text-secondary); transition: background 0.15s, color 0.15s; }
.agenda-nav-btn:hover { background: var(--panel-selected-bg); color: var(--panel-accent-text); }

.agenda-today-chip { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border: 1px solid var(--panel-border); background: var(--panel-bg); color: var(--panel-text-primary); border-radius: 7px; font-size: 11px; font-weight: 600; cursor: pointer; transition: all 0.25s ease; letter-spacing: 0.2px; max-width: 120px; overflow: hidden; }
.agenda-today-chip:hover { border-color: var(--panel-accent); color: var(--panel-accent-text); background: var(--panel-selected-bg); }
.agenda-today-chip--hidden { opacity: 0; max-width: 0; padding: 0 0; border-color: transparent; overflow: hidden; pointer-events: none; transform: scale(0.85); margin: 0; gap: 0; }
.agenda-today-chip-icon { display: none; }

.agenda-view-segmented { display: flex; border-radius: 8px; border: 1px solid var(--panel-border); background: var(--panel-bg); padding: 2px; gap: 2px; }
.agenda-view-seg-btn { display: flex; align-items: center; justify-content: center; gap: 4px; padding: 4px 12px; border: none; background: transparent; border-radius: 6px; font-size: 11px; font-weight: 500; cursor: pointer; color: var(--panel-text-secondary); transition: all 0.15s; white-space: nowrap; }
.agenda-view-seg-btn svg { flex-shrink: 0; }
.agenda-view-seg-btn:hover:not(.active) { color: var(--panel-text-primary); }
.agenda-view-seg-btn.active { background: var(--panel-accent); color: #ffffff; box-shadow: 0 1px 3px rgba(var(--panel-accent-rgb), 0.25); }

.agenda-add-btn { display: flex; align-items: center; justify-content: center; width: 28px; height: 28px; border: none; background: var(--panel-accent); color: #ffffff; border-radius: 50%; cursor: pointer; transition: opacity 0.15s, transform 0.15s; padding: 0; }
.agenda-add-btn--titlebar-row2 { display: none; }
.agenda-add-btn:hover { opacity: 0.85; }
.agenda-add-btn:active { transform: scale(0.9); }
.agenda-app-body-inner { display: flex; flex: 1; overflow: hidden; }

.agenda-app-sidebar { width: 260px; flex-shrink: 0; border-right: 1px solid var(--panel-border); background: var(--agenda-sidebar-bg); display: flex; flex-direction: column; overflow: hidden; }
.agenda-sidebar-header { padding: 0 14px; min-height: 46px; display: flex; flex-direction: column; justify-content: center; border-bottom: 1px solid var(--panel-border); }
.agenda-sidebar-header-row { display: flex; align-items: center; justify-content: space-between; gap: 6px; }
.agenda-sidebar-date { font-size: 13px; font-weight: 600; color: var(--panel-text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.agenda-sidebar-group-btn { display: flex; align-items: center; justify-content: center; width: 26px; height: 26px; border: none; background: none; border-radius: 6px; cursor: pointer; color: var(--panel-text-secondary); transition: all 0.15s; flex-shrink: 0; padding: 0; }
.agenda-sidebar-group-btn:hover { background: var(--agenda-item-hover); color: var(--panel-text-primary); }
.agenda-sidebar-group-btn--active { color: var(--panel-accent-text); }
.agenda-sidebar-group-btn--hidden { visibility: hidden; pointer-events: none; }

.agenda-sidebar-header-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.agenda-sidebar-shared-toggle { display: flex; align-items: center; cursor: pointer; }

.agenda-sidebar-toggle-track { position: relative; display: inline-block; width: 34px; height: 20px; border-radius: 10px; background: var(--panel-border); transition: background 0.2s; flex-shrink: 0; }
.agenda-sidebar-toggle-track--on { background: var(--panel-accent); }
.agenda-sidebar-toggle-thumb { position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #fff; transition: transform 0.2s; box-shadow: 0 1px 3px rgba(0,0,0,0.15); }
.agenda-sidebar-toggle-track--on .agenda-sidebar-toggle-thumb { transform: translateX(14px); }

.agenda-sidebar-events { flex: 1; overflow-y: auto; padding: 6px 8px; }
.agenda-sidebar-group { margin-bottom: 4px; }
.agenda-sidebar-group-label { display: flex; align-items: center; gap: 6px; padding: 6px 8px 4px; font-size: 11px; font-weight: 600; color: var(--panel-text-secondary); text-transform: uppercase; letter-spacing: 0.5px; }
.agenda-sidebar-group-count { font-size: 10px; font-weight: 500; background: var(--agenda-item-hover); color: var(--panel-text-secondary); padding: 1px 5px; border-radius: 8px; }
.agenda-sidebar-event { display: flex; align-items: flex-start; gap: 8px; padding: 8px; border-radius: 6px; cursor: pointer; transition: background 0.15s; -webkit-user-select: none; user-select: none; }
.agenda-sidebar-event:hover { background: var(--agenda-item-hover); }
.agenda-sidebar-event--completed .agenda-sidebar-event-title { text-decoration: line-through; opacity: 0.5; }
.agenda-sidebar-event-color-bar { width: 4px; border-radius: 2px; flex-shrink: 0; align-self: stretch; }
.agenda-sidebar-event-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.agenda-sidebar-event-time { font-size: 11px; font-weight: 500; color: var(--panel-text-secondary); }
.agenda-sidebar-event-title { font-size: 13px; font-weight: 500; color: var(--panel-text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.agenda-sidebar-event-desc { font-size: 11px; color: var(--panel-text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.agenda-sidebar-event-status { font-size: 9px; font-weight: 600; padding: 2px 5px; border-radius: 3px; flex-shrink: 0; margin-top: 2px; text-transform: uppercase; letter-spacing: 0.3px; }
.agenda-sidebar-event-status--pending { background: rgba(255, 149, 0, 0.12); color: #FF9500; }
.agenda-sidebar-event-status--completed { background: rgba(52, 199, 89, 0.12); color: #34C759; }
html.dark .agenda-sidebar-event-status--pending { background: rgba(255, 149, 0, 0.15); color: #FF9F0A; }
html.dark .agenda-sidebar-event-status--completed { background: rgba(52, 199, 89, 0.15); color: #30D158; }

.agenda-sidebar-event--shared { opacity: 0.75; }
.agenda-sidebar-event-shared-icon { flex-shrink: 0; color: var(--panel-text-secondary); opacity: 0.6; margin-top: 5px; }

.agenda-sidebar-empty { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 20px; color: var(--panel-text-secondary); font-size: 13px; }
.agenda-sidebar-add { margin-top: 4px; padding: 5px 12px; border: none; background: var(--panel-accent); color: #fff; border-radius: 6px; font-size: 12px; font-weight: 500; cursor: pointer; transition: opacity 0.15s; }
.agenda-sidebar-add:hover { opacity: 0.85; }

.agenda-app:has(.agenda-weekday-row) .panel-divider::after { content: ''; position: absolute; left: 0; right: 0; top: 45px; border-bottom: 1px solid var(--panel-border); }
.agenda-app-main { flex: 1; display: flex; flex-direction: column; overflow: hidden; min-width: 0; }
.agenda-weekday-row { display: grid; grid-template-columns: repeat(7, 1fr); border-bottom: 1px solid var(--panel-border); flex-shrink: 0; min-height: 46px; }
.agenda-weekday { text-align: center; font-size: 13px; font-weight: 600; color: var(--panel-text-secondary); text-transform: uppercase; letter-spacing: 0.5px; display: flex; align-items: center; justify-content: center; }

.agenda-calendar { flex: 1; overflow: hidden; display: flex; flex-direction: column; container-type: inline-size; }
.agenda-calendar-grid { display: flex; flex-direction: column; flex: 1; }
.agenda-week-row { display: grid; grid-template-columns: repeat(7, 1fr); flex: 1; position: relative; }
.agenda-multiday-layer { position: absolute; top: 22px; left: 0; right: 0; z-index: 1; pointer-events: none; }
.agenda-multiday-bar { position: absolute; height: 16px; border-radius: 4px; font-size: 10px; font-weight: 500; padding: 0 4px; overflow: hidden; white-space: nowrap; line-height: 16px; pointer-events: auto; cursor: pointer; box-sizing: border-box; display: flex; align-items: center; gap: 3px; background-clip: padding-box; }
.agenda-multiday-bar-title { overflow: hidden; text-overflow: ellipsis; }
.agenda-multiday-bar-radio { width: 7px; height: 7px; border-radius: 50%; border: 1.5px solid currentColor; flex-shrink: 0; opacity: 0.7; cursor: pointer; box-sizing: border-box; transition: background 0.15s, opacity 0.15s; }
.agenda-multiday-bar-radio:hover { opacity: 1; }
.agenda-multiday-bar-radio--done { background: currentColor; opacity: 0.9; }
.agenda-multiday-bar-recurrence { flex-shrink: 0; opacity: 0.7; }
.agenda-multiday-bar--start { border-radius: 6px 0 0 6px; border-left: 3px solid transparent; }
.agenda-multiday-bar--end { border-radius: 0 6px 6px 0; border-right: 3px solid transparent; }
.agenda-multiday-bar--mid { border-radius: 0; }
.agenda-multiday-bar--single { border-radius: 6px; border-left: 3px solid transparent; border-right: 3px solid transparent; }

.agenda-day-cell { display: flex; flex-direction: column; border-right: 1px solid var(--agenda-cell-border); border-bottom: 1px solid var(--agenda-cell-border); min-height: 0; overflow: hidden; cursor: pointer; transition: background 0.1s; }
.agenda-day-cell:nth-child(7n) { border-right: none; }
.agenda-day-cell:hover { background: var(--agenda-item-hover); }
.agenda-day-cell--selected { background: var(--panel-selected-bg); }
.agenda-day-cell--other-month { opacity: 0.35; }
.agenda-day-cell--drag-over { background: rgba(var(--panel-accent-rgb), 0.12); }
.agenda-day-cell-header { padding: 3px 5px 1px; flex-shrink: 0; text-align: right; position: relative; z-index: 2; }
.agenda-day-number { font-size: 12px; font-weight: 400; color: var(--panel-text-primary); line-height: 1; }
.agenda-day-number--today { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: var(--panel-accent); color: #ffffff; font-weight: 600; font-size: 11px; box-shadow: 0 0 0 1.5px var(--panel-bg), 0 1px 4px rgba(0,0,0,0.2); }

.agenda-day-cell-events { flex: 1; overflow: hidden; padding: 1px 3px 2px; padding-top: calc(var(--multiday-offset, 0) * 18px + 1px); display: flex; flex-direction: column; gap: 1px; }
.agenda-day-event { display: flex; align-items: center; gap: 3px; padding: 1px 4px; border-radius: 3px; font-size: 10px; line-height: 1.4; overflow: hidden; white-space: nowrap; cursor: pointer; flex-shrink: 0; -webkit-user-select: none; user-select: none; }
.agenda-day-event--completed { opacity: 0.45; }
.agenda-day-event--completed .agenda-day-event-title { text-decoration: line-through; }
.agenda-day-event-radio { width: 8px; height: 8px; border-radius: 50%; border: 1.5px solid currentColor; flex-shrink: 0; opacity: 0.7; cursor: pointer; box-sizing: border-box; transition: background 0.15s, opacity 0.15s; }
.agenda-day-event-radio:hover { opacity: 1; }
.agenda-day-event-radio--done { background: currentColor; opacity: 0.9; }
.agenda-day-event-time { font-weight: 600; flex-shrink: 0; opacity: 0.85; }
.agenda-day-event-title { overflow: hidden; text-overflow: ellipsis; }
.agenda-day-more { font-size: 10px; color: var(--panel-text-secondary); padding: 0 4px; cursor: pointer; flex-shrink: 0; }
.agenda-day-more:hover { color: var(--panel-accent-text); }

.agenda-day-view { flex: 1; overflow-y: auto; overflow-x: hidden; display: flex; flex-direction: column; overscroll-behavior: contain; }
.agenda-day-view-timeline { flex: 1; overflow-y: auto; overflow-x: hidden; }
.agenda-day-hour-row { display: flex; min-height: 48px; border-bottom: 1px solid var(--agenda-cell-border); transition: background 0.1s; }
.agenda-day-hour-row--drag-over { background: rgba(var(--panel-accent-rgb), 0.08); }
.agenda-day-hour-label { width: 56px; flex-shrink: 0; padding: 6px 8px 0; font-size: 11px; font-weight: 500; color: var(--panel-text-secondary); text-align: right; }
.agenda-day-hour-content { flex: 1; min-width: 0; padding: 4px 8px; display: flex; flex-direction: column; gap: 3px; border-left: 1px solid var(--agenda-cell-border); overflow: hidden; }
.agenda-day-allday-section { padding: 8px; display: flex; flex-direction: column; gap: 4px; border-bottom: 2px solid var(--agenda-cell-border); }
.agenda-day-allday-label { font-size: 11px; font-weight: 600; color: var(--panel-text-secondary); text-transform: uppercase; letter-spacing: 0.3px; padding: 0 4px 2px; }
.agenda-day-allday-bar { display: flex; align-items: center; gap: 6px; padding: 6px 10px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; -webkit-user-select: none; user-select: none; min-width: 0; transition: opacity 0.15s; }
.agenda-day-allday-bar:hover { opacity: 0.85; }
.agenda-day-allday-bar--completed { opacity: 0.5; }
.agenda-day-allday-bar--completed .agenda-day-allday-bar-title { text-decoration: line-through; }
.agenda-day-allday-bar-radio { width: 14px; height: 14px; border-radius: 50%; border: 2px solid currentColor; flex-shrink: 0; cursor: pointer; box-sizing: border-box; opacity: 0.6; transition: opacity 0.15s; }
.agenda-day-allday-bar-radio:hover { opacity: 1; }
.agenda-day-allday-bar-radio--done { background: currentColor; opacity: 0.8; }
.agenda-day-allday-bar-recurrence { flex-shrink: 0; opacity: 0.6; }
.agenda-day-allday-bar-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.agenda-day-allday-bar-time { font-size: 11px; opacity: 0.75; margin-left: auto; flex-shrink: 0; white-space: nowrap; }
.agenda-day-hour-event { display: flex; align-items: center; gap: 8px; padding: 5px 10px; border-left: 3px solid var(--panel-accent); border-radius: 4px; background: var(--agenda-item-hover); cursor: pointer; transition: background 0.15s; -webkit-user-select: none; user-select: none; min-width: 0; }
.agenda-day-hour-event:hover { background: var(--panel-selected-bg); }
.agenda-day-hour-event--completed { opacity: 0.5; }
.agenda-day-hour-event--completed .agenda-day-hour-event-title { text-decoration: line-through; }
.agenda-day-hour-event-radio { width: 14px; height: 14px; border-radius: 50%; border: 2px solid var(--panel-text-secondary); flex-shrink: 0; cursor: pointer; box-sizing: border-box; transition: background 0.15s, border-color 0.15s; }
.agenda-day-hour-event-radio:hover { border-color: #34C759; }
.agenda-day-hour-event-radio--done { background: #34C759; border-color: #34C759; }
.agenda-day-hour-event-time { font-size: 12px; font-weight: 600; color: var(--panel-text-secondary); min-width: 36px; }
.agenda-day-hour-event-title { font-size: 13px; font-weight: 500; color: var(--panel-text-primary); flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
html.dark .agenda-day-hour-event-radio { border-color: var(--panel-text-secondary); }
html.dark .agenda-day-hour-event-radio--done { background: #30D158; border-color: #30D158; }


.agenda-context-backdrop { position: fixed; inset: 0; z-index: 100009; }
.agenda-context-backdrop--blur { backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); background: rgba(0, 0, 0, 0.15); }
html.dark .agenda-context-backdrop--blur { background: rgba(0, 0, 0, 0.3); }
.agenda-context-menu { background: #ffffff; border: 1px solid #e5e5ea; border-radius: 8px; padding: 4px 0; min-width: 180px; box-shadow: 0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06); font-size: 13px; user-select: none; }
.agenda-context-menu .context-menu-item { display: flex; align-items: center; padding: 6px 12px; cursor: pointer; color: #1c1c1e; position: relative; gap: 8px; white-space: nowrap; }
.agenda-context-menu .context-menu-item:hover { background: rgba(var(--panel-accent-rgb), 0.08); }
.agenda-context-menu .context-menu-item--danger { color: #FF3B30; }
.agenda-context-menu .context-menu-item--disabled { opacity: 0.4; cursor: default; pointer-events: none; }
.agenda-context-menu .context-menu-icon { width: 18px; display: flex; align-items: center; justify-content: center; }
.agenda-context-menu .context-menu-divider { height: 1px; background: #e5e5ea; margin: 4px 0; }
html.dark .agenda-context-menu { background: #1E2027; border-color: #26272F; box-shadow: 0 8px 30px rgba(0,0,0,0.4); }
html.dark .agenda-context-menu .context-menu-item { color: #F5F5F5; }
html.dark .agenda-context-menu .context-menu-item--danger { color: #FF453A; }
html.dark .agenda-context-menu .context-menu-divider { background: #26272F; }

.agenda-form-dialog, .agenda-reminder-dialog { --agenda-item-hover: #f5f5f5; }
html.dark .agenda-form-dialog, html.dark .agenda-reminder-dialog { --agenda-item-hover: #1E2027; }
html.midnight .agenda-form-dialog, html.midnight .agenda-reminder-dialog { --agenda-item-hover: #241F1B; }
html.cream .agenda-form-dialog, html.cream .agenda-reminder-dialog { --agenda-item-hover: #F0EBE3; }
.agenda-form-dialog { display: flex; flex-direction: column; }
.agenda-reminder-dialog { padding: 16px; }
.agenda-form-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid var(--panel-border, #e5e5ea); flex-shrink: 0; }
.agenda-form-title, .agenda-reminder-title { font-size: 15px; font-weight: 600; color: var(--panel-text-primary, #1c1c1e); }
.agenda-reminder-title { margin-bottom: 12px; }
.agenda-form-close { display: flex; align-items: center; justify-content: center; width: 28px; height: 28px; border: none; background: none; border-radius: 6px; cursor: pointer; color: var(--panel-text-secondary); transition: background 0.15s; }
.agenda-form-close:hover { background: rgba(255,59,48,0.1); color: #FF3B30; }
.agenda-form-body { padding: 16px; overflow-y: auto; flex: 1; }
.agenda-form-field { margin-bottom: 14px; }
.agenda-form-field--half { flex: 1; min-width: 0; }
.agenda-form-field label, .agenda-reminder-field label { display: block; font-size: 12px; font-weight: 500; color: var(--panel-text-secondary); margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.3px; }
.agenda-form-input, .agenda-form-textarea, .agenda-form-select, .agenda-reminder-input { width: 100%; max-width: 100%; padding: 8px 11px; border: 1px solid var(--panel-border); border-radius: 8px; font-size: 13px; color: var(--panel-text-primary); background: var(--panel-bg); outline: none; transition: border-color 0.15s; box-sizing: border-box; min-height: 36px; min-width: 0; text-align: left; -webkit-appearance: none; appearance: none; }
input[type="datetime-local"].agenda-form-input { display: block; }
input[type="datetime-local"].agenda-form-input::-webkit-date-and-time-value { text-align: left; }
.agenda-form-input:focus, .agenda-form-textarea:focus, .agenda-form-select:focus, .agenda-reminder-input:focus { border-color: var(--panel-accent); }
.agenda-form-textarea { resize: vertical; min-height: 72px; }
.agenda-form-row { display: flex; gap: 12px; }
.agenda-form-colors { display: flex; gap: 8px; flex-wrap: wrap; }
.agenda-form-color-dot { width: 20px; height: 20px; border-radius: 50%; cursor: pointer; transition: transform 0.15s, box-shadow 0.15s; border: 2.5px solid transparent; box-sizing: border-box; }
.agenda-form-color-dot:hover { transform: scale(1.2); }
.agenda-form-color-dot--active { border-color: var(--panel-bg, #ffffff); box-shadow: 0 0 0 2px #636366; transform: scale(1.15); }
html.dark .agenda-form-color-dot--active { border-color: var(--panel-bg, #1c1c1e); box-shadow: 0 0 0 2px #98989f; }

.agenda-form-segmented { display: flex; border-radius: 8px; background: var(--agenda-item-hover, #f5f5f5); padding: 2px; gap: 2px; }
.agenda-form-segmented-btn { flex: 1; display: flex; align-items: center; justify-content: center; gap: 5px; padding: 6px 8px; border: none; background: transparent; border-radius: 6px; font-size: 12px; font-weight: 500; cursor: pointer; color: var(--panel-text-secondary); transition: all 0.15s; white-space: nowrap; }
.agenda-form-segmented-btn svg { flex-shrink: 0; }
.agenda-form-segmented-btn:hover:not(.active) { color: var(--panel-text-primary); }
.agenda-form-segmented-btn.active { background: var(--panel-bg); color: var(--panel-accent-text); box-shadow: 0 1px 3px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04); }
html.dark .agenda-form-segmented-btn.active { box-shadow: 0 1px 3px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.06); }

.agenda-form-error, .agenda-reminder-error { padding: 6px 10px; background: rgba(255,59,48,0.08); border-radius: 6px; font-size: 12px; color: #FF3B30; margin-bottom: 10px; }
.agenda-form-field-error { font-size: 11px; color: #FF3B30; margin-top: 4px; padding: 0 2px; }
.agenda-form-input--error, .agenda-form-input--error:focus { border-color: #FF3B30; }
.el-date-editor.agenda-form-input--error .el-input__wrapper { box-shadow: 0 0 0 1px #FF3B30 inset; }
.agenda-reminder-error { margin-top: 8px; }
.agenda-reminder-field { margin-bottom: 12px; }
.agenda-form-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 16px; border-top: 1px solid var(--panel-border); flex-shrink: 0; }
.agenda-form-btn, .agenda-reminder-btn { padding: 8px 18px; border: none; border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer; transition: background 0.15s; min-height: 36px; }
.agenda-form-btn--cancel, .agenda-reminder-btn--cancel { background: var(--agenda-item-hover); color: var(--panel-text-primary); }
.agenda-form-btn--cancel:hover, .agenda-reminder-btn--cancel:hover { background: var(--panel-border); }
.agenda-form-btn--save, .agenda-reminder-btn--save { background: var(--panel-accent); color: #fff; }
.agenda-form-btn--save:hover, .agenda-reminder-btn--save:hover { opacity: 0.85; }
.agenda-form-btn--save:disabled { opacity: 0.6; cursor: not-allowed; }
.agenda-reminder-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; }

.agenda-form-dialog .el-date-editor .el-input__wrapper,
.agenda-form-dialog .el-select .el-input__wrapper,
.agenda-form-dialog .el-select .el-select__wrapper,
.agenda-reminder-dialog .el-select .el-input__wrapper,
.agenda-reminder-dialog .el-select .el-select__wrapper { min-height: 36px; padding: 4px 11px; }
.agenda-form-reminder-item .el-select__wrapper,
.agenda-reminder-item .el-select__wrapper { background: #ffffff; border: 1px solid #d1d1d6; border-radius: 8px; box-shadow: none; font-size: 13px; transition: border-color 0.15s; }
html.dark .agenda-form-reminder-item .el-select__wrapper,
html.dark .agenda-reminder-item .el-select__wrapper { background: #15171C !important; border-color: #26272F; box-shadow: none; }
html.midnight .agenda-form-reminder-item .el-select__wrapper,
html.midnight .agenda-reminder-item .el-select__wrapper { background: #1A1714 !important; border-color: #2C2823; box-shadow: none; }
html.cream .agenda-form-reminder-item .el-select__wrapper,
html.cream .agenda-reminder-item .el-select__wrapper { background: #FEFCF8 !important; border-color: #DDD7CC; box-shadow: none; }
.agenda-form-dialog .el-select__tags { gap: 2px; }
.agenda-form-dialog .el-date-editor { --el-date-editor-width: 100%; }

.agenda-form-view-title { display: flex; align-items: center; gap: 8px; font-size: 16px; font-weight: 600; color: var(--panel-text-primary); margin-bottom: 12px; }
.agenda-form-view-color { width: 4px; height: 20px; border-radius: 2px; flex-shrink: 0; }
.agenda-form-view-meta { display: flex; align-items: center; gap: 6px; font-size: 13px; color: var(--panel-text-secondary); margin-bottom: 6px; }
.agenda-form-view-meta svg { flex-shrink: 0; }
.agenda-form-view-desc { font-size: 13px; color: var(--panel-text-primary); line-height: 1.5; margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--panel-border); white-space: pre-wrap; word-break: break-word; }
.agenda-form-view-status { margin-top: 12px; }

.agenda-app-popper.el-popper { z-index: 200025 !important; }

@media (max-width: 500px) {
    .base-modal-dialog.agenda-form-dialog { max-width: 96vw !important; max-height: 90vh !important; }
    .agenda-form-row { flex-direction: column; gap: 0; }
}

/* No sidebar (< 670px) */
.agenda-app-panel--no-sidebar .panel-titlebar-left > .panel-titlebar-btn:first-child { display: none; }

/* Static center (< 800px) */
.agenda-app-panel--static-center .panel-titlebar-left { z-index: auto; }
.agenda-app-panel--static-center .panel-titlebar-actions { z-index: auto; }
.agenda-app-panel--static-center .agenda-app-titlebar-center { position: static; transform: none; }

/* Short panel (<= 575px height) */
.agenda-app-panel--short .agenda-weekday { font-size: 11px; }
.agenda-app-panel--short .agenda-weekday-row { min-height: 30px; }

@container (max-width: 410px) {
    .agenda-day-more-text { display: none; }
}

/* Narrow panel (< 620px) */
.agenda-app-panel--narrow .agenda-nav-label { width: auto; }
.agenda-app-panel--narrow .agenda-today-chip { padding: 0; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; gap: 0; font-size: 0; }
.agenda-app-panel--narrow .agenda-today-chip-icon { display: block; }
.agenda-app-panel--narrow .agenda-today-chip-text { display: none; }
.agenda-app-panel--narrow .agenda-view-seg-btn { font-size: 0; padding: 6px 8px; gap: 0; }

.agenda-app.panel-app--compact .panel-titlebar { flex-wrap: wrap; padding: 6px 8px; gap: 6px; }

.agenda-app.panel-app--compact .panel-titlebar-left { display: contents; }
.agenda-app.panel-app--compact .panel-titlebar-actions { display: contents; }

.agenda-app.panel-app--compact .panel-titlebar-left > .panel-titlebar-btn { display: none; }

.agenda-app.panel-app--compact .agenda-today-chip,
.agenda-app.panel-app--compact .panel-titlebar-actions > .panel-titlebar-btn { padding: 0; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; border-radius: 8px; color: var(--panel-text-secondary); transition: background 0.15s, color 0.15s, border-color 0.15s; }

.agenda-app.panel-app--compact .agenda-today-chip,
.agenda-app.panel-app--compact .panel-titlebar-actions > .panel-titlebar-btn { background: var(--panel-bg); border: 1px solid var(--panel-border); }

.agenda-app.panel-app--compact .agenda-add-btn--row1 { width: 26px; height: 26px; border-radius: 50%; }

.agenda-app.panel-app--compact .agenda-today-chip:hover,
.agenda-app.panel-app--compact .panel-titlebar-actions > .panel-titlebar-btn:hover { background: var(--panel-selected-bg); color: var(--panel-text-primary); border-color: rgba(var(--panel-accent-rgb), 0.2); }
.agenda-app.panel-app--compact .panel-titlebar-actions > .panel-titlebar-btn.panel-titlebar-btn--close:hover { background: rgba(255,59,48,0.1); color: #FF3B30; }

.agenda-app.panel-app--compact .agenda-add-btn--row1 { display: none; }

.agenda-app.panel-app--compact .agenda-nav-group { order: 1; border: none; background: none; padding: 0; margin-right: auto; }
.agenda-app.panel-app--compact .agenda-nav-label { width: auto; font-size: 13px; font-weight: 700; text-align: center; padding: 0 2px; }
.agenda-app.panel-app--compact .agenda-nav-btn { width: 28px; height: 28px; }

.agenda-app.panel-app--compact .agenda-today-chip { order: 3; font-size: 0; letter-spacing: 0; margin-right: 2px; }
.agenda-app.panel-app--compact .agenda-today-chip--hidden { visibility: hidden; width: 0; min-width: 0; padding: 0; border: none; margin: 0; overflow: hidden; }
.agenda-app.panel-app--compact .agenda-today-chip-icon { display: flex; }
.agenda-app.panel-app--compact .agenda-today-chip-text { display: none; }

.agenda-app.panel-app--compact .panel-titlebar-actions > .panel-titlebar-btn:not(.panel-titlebar-btn--close) { order: 4; margin-right: 2px; }

.agenda-app.panel-app--compact .panel-titlebar-btn--close { order: 5; }

.agenda-app.panel-app--compact .agenda-app-titlebar-center { order: 10; position: static; transform: none; width: calc(100% + 16px); margin: 2px -8px -2px; padding: 5px 8px; box-sizing: border-box; border-top: 1px solid var(--agenda-cell-border); display: flex; justify-content: space-between; align-items: center; }
.agenda-app.panel-app--compact .agenda-titlebar-center-left { display: flex; align-items: center; gap: 6px; }
.agenda-app.panel-app--compact .agenda-add-btn--titlebar-row2 { display: flex; width: 24px; height: 24px; }
.agenda-app.panel-app--compact .agenda-add-btn--titlebar-row2 svg { width: 10px; height: 10px; }
.agenda-app.panel-app--compact .agenda-titlebar-center-right { display: flex; align-items: center; gap: 6px; cursor: pointer; -webkit-user-select: none; user-select: none; }
.agenda-app.panel-app--compact .agenda-view-segmented { flex: 0 1 auto; }
.agenda-app.panel-app--compact .agenda-view-seg-btn { padding: 5px 20px; font-size: 12px; }
.agenda-app.panel-app--compact .agenda-add-btn--row1 { visibility: hidden; pointer-events: none; }

.agenda-app.panel-app--compact .agenda-app-main { flex: 1; }

.agenda-app-sidebar--sheet { position: absolute; bottom: 0; left: 0; right: 0; width: 100%; max-height: none; border-right: none; border-top: none; border-bottom: none; z-index: 10; border-radius: 14px 14px 0 0; box-shadow: 0 -4px 20px rgba(0,0,0,0.1); transition: height 0.25s ease-out, border-radius 0.25s ease-out; }
.agenda-app-sidebar--sheet.agenda-app-sidebar--dragging { transition: none; }
.agenda-app-sidebar--sheet.agenda-app-sidebar--closing { transition: height 0.25s ease-in; overflow: hidden; pointer-events: none; }
.agenda-app-sidebar--drag-over { box-shadow: inset 0 0 0 2px rgba(var(--panel-accent-rgb), 0.3); }
.agenda-app-sidebar--sheet.agenda-app-sidebar--drag-over .agenda-sidebar-header { background: var(--agenda-sidebar-bg); box-shadow: inset 2px 0 0 rgba(var(--panel-accent-rgb), 0.3), inset -2px 0 0 rgba(var(--panel-accent-rgb), 0.3), inset 0 2px 0 rgba(var(--panel-accent-rgb), 0.3); border-radius: 14px 14px 0 0; }
.agenda-app-sidebar--sheet .agenda-sidebar-header { cursor: grab; touch-action: none; padding: 6px 14px 8px; min-height: auto; }
.agenda-app-sidebar--sheet .agenda-sidebar-header:active { cursor: grabbing; }

.agenda-sidebar-handle { width: 36px; height: 4px; border-radius: 2px; background: var(--panel-resize-handle); margin: 0 auto 6px; }

.agenda-app-sidebar--fullscreen { height: 100% !important; border-radius: 0; }

.agenda-swipe-container { position: relative; overflow: hidden; }
.agenda-swipe-action { position: absolute; top: 0; bottom: 0; display: flex; align-items: center; pointer-events: none; }
.agenda-swipe-action--complete { left: 0; width: 50%; background: #34C759; justify-content: flex-start; padding-left: 20px; border-radius: 6px; }
.agenda-swipe-action--pending { left: 0; width: 50%; background: #FF9500; justify-content: flex-start; padding-left: 20px; border-radius: 6px; }
.agenda-swipe-action--delete { right: 0; width: 50%; background: #FF3B30; justify-content: flex-end; padding-right: 20px; border-radius: 6px; }
html.dark .agenda-swipe-action--complete { background: #30D158; }
html.dark .agenda-swipe-action--pending { background: #FF9F0A; }
html.dark .agenda-swipe-action--delete { background: #FF453A; }
.agenda-swipe-container .agenda-sidebar-event { position: relative; z-index: 1; background: var(--agenda-sidebar-bg); transition: transform 0.25s ease-out; }
.agenda-sidebar-event--drag-ready { cursor: grab; box-shadow: 0 4px 12px rgba(0,0,0,0.18); transform: scale(1.02); transition: transform 0.15s ease, box-shadow 0.15s ease; }
html.dark .agenda-sidebar-event--drag-ready { box-shadow: 0 4px 12px rgba(0,0,0,0.4); }

.agenda-tooltip {
    position: fixed;
    z-index: calc(var(--panel-z-base, 100000) + 25);
    max-width: 280px;
    min-width: 160px;
    background: #ffffff;
    border: 1px solid #e5e5ea;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06);
    display: flex;
    overflow: hidden;
    pointer-events: none;
    transform: translateX(-50%) translateY(-100%) translateY(-8px);
    animation: agenda-tooltip-in 0.15s ease-out;
}

@keyframes agenda-tooltip-in {
    from { opacity: 0; transform: translateX(-50%) translateY(-100%) translateY(-4px); }
    to { opacity: 1; transform: translateX(-50%) translateY(-100%) translateY(-8px); }
}

.agenda-tooltip-color {
    width: 4px;
    flex-shrink: 0;
}

.agenda-tooltip-body {
    padding: 8px 10px;
    min-width: 0;
}

.agenda-tooltip-title {
    font-size: 13px;
    font-weight: 600;
    color: #1c1c1e;
    line-height: 1.3;
    word-break: break-word;
    display: flex;
    align-items: flex-start;
    gap: 4px;
}

.agenda-tooltip-shared-icon {
    flex-shrink: 0;
    margin-top: 1px;
    color: #8e8e93;
}
html.dark .agenda-tooltip-shared-icon { color: #8e8e93; }

.agenda-tooltip-time {
    font-size: 11px;
    color: #8e8e93;
    margin-top: 2px;
}

.agenda-tooltip-desc {
    font-size: 12px;
    color: #636366;
    margin-top: 4px;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
}

html.dark .agenda-tooltip {
    background: #1E2027;
    border-color: #26272F;
    box-shadow: 0 4px 16px rgba(0,0,0,0.4);
}
html.dark .agenda-tooltip-title { color: #F5F5F5; }
html.dark .agenda-tooltip-time { color: #8e8e93; }
html.dark .agenda-tooltip-desc { color: #aeaeb2; }

html.midnight .agenda-context-menu { background: #241F1B; border-color: #2C2823; box-shadow: 0 8px 30px rgba(0,0,0,0.4); }
html.midnight .agenda-context-menu .context-menu-item { color: #EAE6E1; }
html.midnight .agenda-context-menu .context-menu-item:hover { background: rgba(200, 134, 60, 0.12); }
html.midnight .agenda-context-menu .context-menu-divider { background: #2C2823; }

html.midnight .agenda-tooltip { background: #241F1B; border-color: #2C2823; box-shadow: 0 4px 16px rgba(0,0,0,0.4); }
html.midnight .agenda-tooltip-title { color: #EAE6E1; }
html.midnight .agenda-tooltip-desc { color: #A9A39D; }

/* Recurrence icon */
.agenda-day-event-recurrence { flex-shrink: 0; opacity: 0.7; }
.agenda-sidebar-event-recurrence { margin-left: 4px; flex-shrink: 0; opacity: 0.5; vertical-align: middle; }
.agenda-day-hour-event-recurrence { flex-shrink: 0; opacity: 0.5; margin-right: 2px; }

/* Form: Reminders */
.agenda-form-reminders { display: flex; flex-direction: column; gap: 8px; }
.agenda-form-reminder-item { display: flex; flex-direction: column; gap: 6px; background: rgba(0, 0, 0, 0.06); border-radius: 8px; padding: 8px; }
html.dark .agenda-form-reminder-item { background: rgba(0, 0, 0, 0.3); }
html.midnight .agenda-form-reminder-item { background: rgba(0, 0, 0, 0.25); }
html.cream .agenda-form-reminder-item { background: rgba(0, 0, 0, 0.045); }
.agenda-form-reminder-remove { display: flex; align-items: center; justify-content: center; width: 100%; border: 1px dashed rgba(255, 59, 48, 0.35); background: none; color: #FF3B30; cursor: pointer; padding: 4px 0; font-size: 11px; font-weight: 500; border-radius: 6px; transition: border-color 0.15s, background 0.15s; }
.agenda-form-reminder-remove:hover { border-color: #FF3B30; background: rgba(255, 59, 48, 0.06); }
html.dark .agenda-form-reminder-remove { border-color: rgba(255, 69, 58, 0.3); color: #FF453A; }
html.dark .agenda-form-reminder-remove:hover { border-color: #FF453A; }
html.midnight .agenda-form-reminder-remove { border-color: rgba(255, 69, 58, 0.25); color: #FF453A; }
html.cream .agenda-form-reminder-remove { border-color: rgba(200, 50, 40, 0.25); color: #C83228; }
.agenda-form-btn--add { margin: 10px 0; }
.agenda-form-field--recurrence { margin-top: 8px; margin-bottom: 24px; }
.agenda-form-field--visibility { margin-top: 18px; }
.agenda-form-btn--add { display: flex; align-items: center; justify-content: center; gap: 4px; width: 100%; padding: 6px 12px; border: 1px dashed var(--panel-border); border-radius: 8px; background: none; color: var(--panel-text-secondary); font-size: 12px; font-weight: 500; cursor: pointer; transition: color 0.15s, border-color 0.15s; }
.agenda-form-btn--add:hover { color: var(--panel-accent-text); border-color: var(--panel-accent); }

/* Form: Recurrence */
.agenda-form-recurrence-toggle { display: flex; align-items: center; justify-content: space-between; cursor: pointer; padding: 8px 10px; user-select: none; font-size: 13px; color: var(--panel-text-primary); background: var(--agenda-item-hover); border-radius: 8px; }
.agenda-form-switch { position: relative; width: 34px; height: 20px; border-radius: 10px; background: var(--panel-border); flex-shrink: 0; transition: background 0.2s; cursor: pointer; }
.agenda-form-switch-knob { position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #fff; transition: transform 0.2s; box-shadow: 0 1px 3px rgba(0,0,0,0.15); }
.agenda-form-switch--on { background: var(--panel-accent); }
.agenda-form-switch--on .agenda-form-switch-knob { transform: translateX(14px); }
.agenda-form-recurrence-fields { margin-top: 8px; display: flex; flex-direction: column; gap: 6px; }
.agenda-form-recurrence-freq-row { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--panel-text-primary); flex-wrap: wrap; }
.agenda-form-recurrence-freq-seg { flex: 1; min-width: 0; }
.agenda-form-recurrence-freq-seg .agenda-form-segmented-btn { padding: 5px 6px; font-size: 11px; }
.agenda-form-recurrence-interval { width: 56px; min-height: 30px; padding: 4px 6px; text-align: center; font-size: 13px; }
.agenda-form-recurrence-hint { display: flex; align-items: center; gap: 6px; font-size: 11px; color: #3478A7; background: rgba(0, 122, 255, 0.08); border-radius: 6px; padding: 5px 8px; }
.agenda-form-recurrence-hint svg { flex-shrink: 0; opacity: 0.7; }
html.dark .agenda-form-recurrence-hint { color: #5AC8FA; background: rgba(90, 200, 250, 0.1); }
html.midnight .agenda-form-recurrence-hint { color: #6BB3E0; background: rgba(107, 179, 224, 0.1); }
html.cream .agenda-form-recurrence-hint { color: #5A8A6A; background: rgba(90, 138, 106, 0.08); }
.agenda-form-recurrence-end-label { margin-top: 6px; }
.agenda-form-recurrence-count-row { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--panel-text-primary); }
.agenda-form-recurrence-count { width: 64px; min-height: 30px; padding: 4px 6px; text-align: center; font-size: 13px; }


/* ReminderPicker */
.agenda-reminder-list { display: flex; flex-direction: column; gap: 6px; margin: 8px 0; max-height: 200px; overflow-y: auto; }
.agenda-reminder-item { display: flex; flex-direction: column; gap: 6px; background: rgba(0, 0, 0, 0.06); border-radius: 8px; padding: 8px; }
html.dark .agenda-reminder-item { background: rgba(0, 0, 0, 0.3); }
html.midnight .agenda-reminder-item { background: rgba(0, 0, 0, 0.25); }
html.cream .agenda-reminder-item { background: rgba(0, 0, 0, 0.045); }
.agenda-reminder-remove-btn { display: flex; align-items: center; justify-content: center; width: 100%; border: 1px dashed rgba(255, 59, 48, 0.35); background: none; color: #FF3B30; cursor: pointer; padding: 4px 0; font-size: 11px; font-weight: 500; border-radius: 6px; transition: border-color 0.15s, background 0.15s; }
.agenda-reminder-remove-btn:hover { border-color: #FF3B30; background: rgba(255, 59, 48, 0.06); }
html.dark .agenda-reminder-remove-btn { border-color: rgba(255, 69, 58, 0.3); color: #FF453A; }
html.dark .agenda-reminder-remove-btn:hover { border-color: #FF453A; }
html.midnight .agenda-reminder-remove-btn { border-color: rgba(255, 69, 58, 0.25); color: #FF453A; }
html.cream .agenda-reminder-remove-btn { border-color: rgba(200, 50, 40, 0.25); color: #C83228; }
.agenda-reminder-add-btn { margin-top: 8px; }
.agenda-reminder-empty { font-size: 12px; color: var(--panel-text-secondary); text-align: center; padding: 8px 0; }

.panel-content--transparent .agenda-app-sidebar { background: transparent; }
.panel-content--transparent .agenda-app-sidebar--sheet { background: rgba(249, 249, 251, 0.9); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); }
html.dark .panel-content--transparent .agenda-app-sidebar--sheet { background: rgba(27, 28, 34, 0.9); }
html.midnight .panel-content--transparent .agenda-app-sidebar--sheet { background: rgba(32, 29, 25, 0.9); }
html.cream .panel-content--transparent .agenda-app-sidebar--sheet { background: rgba(245, 241, 234, 0.9); }
.panel-content--transparent .agenda-day-cell:hover { background: rgba(128, 128, 128, 0.08); }
.panel-content--transparent .agenda-day-hour-event:hover { background: rgba(128, 128, 128, 0.08); }

</style>
